import {EventEmitter, inject, Injectable, Injector} from '@angular/core';
import {
  AuthControllerService,
  BimBaseCheckControllerService,
  HoaiCheckControllerService,
  IfcControllerService,
  MyCheckFeatureRequestRequest,
  ProjectFile,
  ProjectFileControllerService,
  QualityControllerService,
  SimpleCheck,
  UserControllerService,
  WorkflowDefinitionControllerService
} from "../../../api";
import {StateConfigService} from "../../../services/state/state-config.service";
import {from, Observable, of, Subscription, switchMap, throwError} from "rxjs";
import {catchError, filter, map, tap, timeout} from "rxjs/operators";
import {NavigationEnd, Router} from "@angular/router";
import {MessageViewComponent} from "../message-view/message-view.component";
import {ChatStreamService} from '../../../services/chat/chat-stream.service';
import {ChatStreamEvent, ChatToolStep} from '../../../services/chat/chat-stream.model';
import {BimViewerService} from '../../../services/bim-viewer/bim-viewer.service';
import {ChatViewerAction, ChatViewerQuery} from '../../../services/bim-viewer/bim-viewer-chat.model';
import {
  ChatApiService,
  ChatMycheckSeriesDto,
  ChatQuotaDto,
  ChatThreadMode as ApiChatThreadMode
} from '../../../services/chat/chat-api.service';
import {ChatContextEntityKind, ChatContextLabelService} from '../../../services/chat/chat-context-label.service';
import {ChatPageContextService} from '../../../services/chat/chat-page-context.service';
import {FeatureProposalApi} from './feature-proposal.api';

/** So lange darf die KI für einen Feature-Vorschlag brauchen, danach öffnet das Formular leer. */
export const FEATURE_PROPOSAL_TIMEOUT_MS = 60_000;

export interface PlatformContext {
  route: string;
  module: string;
  projectId?: number;
  checkId?: string;
  checkType?: 'BIM' | 'LOI' | 'HOAI' | 'QUALITY';
  /**
   * Das konkrete Objekt, auf dem der Nutzer steht – ein myCheck, ein Prüflauf, eine Aufgabe.
   *
   * <p>Ergänzt das Modul: „myChecks" sagt, wo man ist, aber nicht, woran man arbeitet. Der Name
   * dazu wird getrennt aufgelöst ({@link ChatContextLabelService}), die Kennung selbst erreicht die
   * Oberfläche nie.
   */
  entityKind?: ChatContextEntityKind;
  entityId?: string;
  /** Gesetzt, solange der 3D-Viewer eine IFC-Datei offen hat. */
  viewerFileId?: string;
  viewerFileName?: string;
  viewerModelCount?: number;
}

/** Ein im Viewer geladenes Modell (.frag-URL, wie sie das IFC-MCP-Tool erwartet). */
export interface ViewerModelRef {
  name: string;
  url: string;
}

/**
 * Was gerade im 3D-Viewer offen ist.
 *
 * <p>Der Viewer liegt unter `/projects/:id/files/:fileId`, also liest `platformModule` dort das
 * URL-Segment `files` und der Bot bekam „Dateien" gemeldet – er wusste nicht, dass der Nutzer vor
 * einem geoeffneten Modell sitzt. `/projects/:id/ifc-viewer` ist dagegen nur der Dateiauswaehler.
 */
export interface ViewerContext {
  fileId?: string;
  fileName?: string;
  projectId?: number;
  /** Hauptmodell zuerst, danach die Koordinationsmodelle. */
  models: ViewerModelRef[];
  isCoordination: boolean;
}

export interface FileReference {
  id: string;
  fileName: string;
  dataId: string;
  fragmentsLink?: string;
}

export interface UiBlockOption {
  label: string;
  value: string;
}

export interface UiBlockSummaryField {
  title: string;
  bulletPoints: string[];
}

/** Wo ein Block steht: Nachricht und Position darin. */
export interface FeatureRequestBlockRef {
  messageId?: string;
  blockIndex?: number;
}

/** Vom Modell vorgeschlagene Feature-Anfrage; der Nutzer sieht und bearbeitet sie vor dem Senden. */
export interface UiBlockFeatureProposal {
  title: string;
  problem: string;
  desiredBehavior: string;
  area: string;
}

export interface UiBlock {
  type: 'question' | 'options' | 'summary' | 'unavailable';
  question?: string;
  options?: UiBlockOption[];
  multiSelect?: boolean;
  summary?: string;
  summaryFields?: UiBlockSummaryField[];
  unavailableText?: string;
  shouldImplement?: boolean;
  /** Fehlt bei älteren Nachrichten oder wenn das Modell keinen Vorschlag geliefert hat. */
  featureProposal?: UiBlockFeatureProposal;
}

export interface Message {
  id: string;
  message: string | undefined,
  createdOn: Date,
  metadata: Record<string, string> | undefined,
  sender: string | undefined,
  uiBlock: UiBlock[] | undefined,
  files: FileReference[] | ProjectFile[] | undefined,
  table: { columns: string[], rows: {} } | undefined

}

type ChatThreadMode = 'classic' | 'mycheck' | 'edit';

/** Ein Chat-Thread, so wie der Service ihn intern führt. */
interface ChatThreadEntry {
  id: string;
  topic: string;
  createdOn: Date;
}

/**
 * Die beiden Chat-Oberflächen der Anwendung.
 *
 * <p>Sie sind zugleich der Wert, der als `mode` in der ACS-Thread- und Nachrichten-Metadata landet
 * und den der Bot auswertet – deshalb bewusst dieselben Bezeichner wie bisher `chatMode`.
 */
export type ChatSurface = ChatThreadMode;

/**
 * Der Zustand **einer** Chat-Oberfläche.
 *
 * <p>Vorher lag das alles direkt auf dem Service, der ein Singleton ist. Der eingebettete
 * myCheck-Chat und der schwebende/ganzseitige klassische Chat teilten sich damit einen einzigen
 * `currentChat`, eine Nachrichtenliste und eine Denk-Sperre: `ensureMycheckThread` ersetzte den
 * offenen Thread, und beide Oberflächen rendern direkt aus diesen Feldern – die myCheck-Konversation
 * erschien also im klassischen Chat.
 *
 * <p>Was hier NICHT liegt: der ACS-Client, das Token, die Thread-Liste und die myCheck-Buchhaltung.
 * Die bleiben global – ein zweiter ACS-Client bzw. eine zweite Thread-Zuordnung würde doppelte
 * Threads für dasselbe myCheck erzeugen.
 */
class ChatSession {
  currentChat: ChatThreadEntry | undefined;
  messages: Message[] = [];
  /** Nur über {@link ChatService.setBotThinking} schreiben – daran hängt der Watchdog. */
  isBotThinking = false;
  botThinkingWatchdog?: number;
  question = '';
  allFiles: ProjectFile[] = [];
  selectedFiles: FileReference[] = [];
  /** Als Kontext gewählte myCheck-Prüfergebnisse (`ChatContext.checkRuns`). */
  selectedRuns: { checkResultId?: string; instanceId?: string; type?: string; name: string }[] = [];
  BIM_Check: SimpleCheck | undefined;
  LOI_Check: SimpleCheck | undefined;
  QUALITY_Check: SimpleCheck | undefined;
  HOAI_Check: SimpleCheck | undefined;
  pendingUiBlockSelection:
    { messageId?: string; blockIndex: number; uiBlockType: UiBlock['type']; optionValues: string[] }
    | undefined;
  /**
   * Die laufenden Zwischenschritte des Agenten („Sieht die Projektdateien durch…").
   *
   * <p>Ersetzt den bisherigen „Tippt…"-Platzhalter: der war reine Vermutung der Oberfläche, hier
   * steht, woran tatsächlich gearbeitet wird.
   */
  toolSteps: ChatToolStep[] = [];

  /** Der offene Antwort-Stream. Beim Wechsel des Verlaufs abzubestellen. */
  streamSubscription?: Subscription;

  /** Der laufende Turn – für den Stopp-Knopf. Gesetzt mit TURN_STARTED, geleert am Ende. */
  currentTurnId?: string;

  /**
   * Die Nachricht, die gerade entsteht.
   *
   * <p>Als Referenz und nicht als Kennung: die Kennung wechselt beim Abschluss von der lokalen
   * `streaming-…` auf die des Servers. Die Ansicht entscheidet damit, in welcher Blase die
   * Zwischenschritte erscheinen – es ist dieselbe, in der anschliessend die Antwort steht.
   */
  streamingMessage?: Message;

  /** Eigener Emitter je Oberfläche: sonst scrollen zwei gemountete Ansichten gemeinsam. */
  readonly messagesScrollDown = new EventEmitter<void>();
  messageViewComponent?: MessageViewComponent;

  constructor(readonly surface: ChatSurface) {
  }
}

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private authControllerService = inject(AuthControllerService);
  private stateConfigService = inject(StateConfigService);
  private chatStreamService = inject(ChatStreamService);
  private userControllerService = inject(UserControllerService);
  private ifcCheckController = inject(IfcControllerService);
  private bimBaseController = inject(BimBaseCheckControllerService);
  private fileControllerService = inject(ProjectFileControllerService);
  private qualityControllerService = inject(QualityControllerService);
  private workflowDefinitionControllerService = inject(WorkflowDefinitionControllerService);
  private featureProposalApi = inject(FeatureProposalApi);
  filter: { lph?: string[] } = {};
  private router = inject(Router);
  private pageContext = inject(ChatPageContextService);

  // --- Floating chat & context tracking ---
  floatingChatOpen = false;
  isFloatingMode = false;
  isInitialized = false;
  readonly defaultPanelWidth = 420;
  panelWidth = this.defaultPanelWidth;
  currentPlatformContext: PlatformContext = {
    route: '',
    module: 'unknown',
  };
  private routerSub?: Subscription;

  /**
   * Modul-Wert, solange der 3D-Viewer offen ist.
   *
   * <p>Bewusst nicht `files` (das ist die Dateiliste **und** der PDF-Viewer) und nicht
   * `ifc-viewer` (das ist der Dateiauswaehler, der den Viewer erst in einem neuen Tab oeffnet).
   */
  static readonly VIEWER_MODULE = 'bim-viewer';

  private activeViewerContext?: ViewerContext;

  /** Was gerade im 3D-Viewer offen ist – oder `undefined`. */
  get viewerContext(): ViewerContext | undefined {
    return this.activeViewerContext;
  }

  /**
   * Meldet dem Chat, dass der Nutzer ein Modell im 3D-Viewer offen hat.
   *
   * <p>Idempotent: das Koordinationsmodell wird nachgeladen (`forkJoin` im `FileViewerComponent`)
   * und darf einfach erneut registrieren. Dateien ohne `fragmentsLink` werden ignoriert – ohne
   * .frag-URL laedt der Viewer nichts und das IFC-MCP-Tool kann nichts damit anfangen.
   */
  setViewerContext(file: ProjectFile, additionalModels: ViewerModelRef[] = []): void {
    // Ein Koordinationsmodell wird inzwischen ueber seine eigene Id geoeffnet. Es traegt selbst
    // keinen fragmentsLink -- seine Modelle stehen komplett in additionalModels.
    const isCoordinationFile = file?.fileType === ProjectFile.FileTypeEnum.COORDINATION;
    if (!isCoordinationFile && (file?.fileType !== ProjectFile.FileTypeEnum.IFC || !file.fragmentsLink)) return;

    const ownModel: ViewerModelRef[] = file.fragmentsLink
      ? [{name: file.name ?? 'Modell', url: file.fragmentsLink}]
      : [];
    const models: ViewerModelRef[] = ownModel
      .concat(additionalModels.filter(model => !!model?.url))
      .filter((model, index, all) => all.findIndex(other => other.url === model.url) === index);
    if (models.length === 0) return;

    this.activeViewerContext = {
      fileId: file.id,
      fileName: file.name,
      projectId: this.currentProject || undefined,
      models,
      isCoordination: isCoordinationFile || models.length > 1,
    };
    // Sofort nachziehen, statt auf das naechste NavigationEnd zu warten: die Registrierung kommt
    // erst, wenn die Datei geladen ist – da ist die Navigation lange vorbei.
    this.updateContextFromUrl(this.router.url);
  }

  /**
   * Gibt den Viewer-Kontext frei.
   *
   * <p>Nur bei passender Datei-ID: beim Wechsel von einem Viewer direkt in den naechsten laeuft das
   * `ngOnDestroy` der alten Ansicht nach der Registrierung der neuen.
   */
  clearViewerContext(fileId?: string): void {
    if (!this.activeViewerContext) return;
    if (fileId && this.activeViewerContext.fileId !== fileId) return;
    this.activeViewerContext = undefined;
    this.updateContextFromUrl(this.router.url);
  }

  toggleFloatingChat(): void {
    this.floatingChatOpen = !this.floatingChatOpen;
  }

  setPanelWidth(width: number): void {
    const maxWidth = window.innerWidth * 0.5;
    this.panelWidth = Math.min(Math.max(width, 360), maxWidth);
  }

  startRouteTracking(): void {
    if (this.routerSub) return;
    this.updateContextFromUrl(this.router.url);
    this.routerSub = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event) => {
      this.updateContextFromUrl((event as NavigationEnd).urlAfterRedirects);
    });
  }

  stopRouteTracking(): void {
    this.routerSub?.unsubscribe();
    this.routerSub = undefined;
  }

  /** Returns true if the user is currently on the full chatBIC page */
  isOnChatPage(): boolean {
    return this.currentPlatformContext.module === 'chat';
  }

  /**
   * Präfix der Platzhalter-Schlüssel. Eine echte myCheck-ID ist eine UUID ohne Präfix, die
   * Unterscheidung ist damit eindeutig – und nötig, weil ein Platzhalter nach einem Reload sonst
   * als `rulesetId` an den Bot ginge und der ihn für einen bestehenden myCheck hielte.
   */
  static readonly PENDING_MYCHECK_PREFIX = 'pending-';

  /** Optionswert, mit dem der Nutzer das Anlegen des besprochenen myChecks bestätigt. */
  static readonly MYCHECK_CONFIRM_CREATE = 'mycheck_confirm_create';
  /** Optionswert, mit dem der Nutzer den Plan einer myCheck-Serie bestätigt (BIC-Backend#189). */
  static readonly MYCHECK_SERIES_CONFIRM = 'mycheck_series_confirm';
  /** Auswahl des automatischen Weiter in einer Serie – der Inhalt kommt serverseitig aus dem Plan. */
  static readonly MYCHECK_SERIES_NEXT = 'mycheck_series_next';
  /** Pause zwischen zwei Schritten einer Serie: lang genug, um das Ergebnis zu lesen oder anzuhalten. */
  private static readonly MYCHECK_SERIES_NEXT_DELAY_MS = 1500;
  /** `EDIT_TIMEOUT_SECONDS` im Bot ist 180s (`BIC-Chat/bot/config.py`); +30s Reserve wie bei mycheck. */
  private static readonly BOT_WATCHDOG_EDIT_MS = 210_000;

  /**
   * Alle Chat-Oberflächen.
   *
   * <p>Als Konstante, weil sie an mehreren Stellen vollständig durchlaufen werden müssen
   * (Aufräumen, Projektwechsel). Eine aufgezählte Liste im Code hat beim Hinzukommen des
   * Edit-Modus genau dort Lücken hinterlassen.
   */
  /**
   * Pfadsegmente, die auf ein Modul folgen können, ohne ein Objekt zu bezeichnen.
   *
   * <p>Ohne diese Liste hielte die Route {@code /projects/1/my-checks/new} das Wort „new" für eine
   * Regelwerk-Kennung, und die Auflösung liefe bei jeder Neuanlage ins Leere.
   */
  /**
   * Der Name des Objekts, auf dem der Nutzer steht – oder {@code undefined}.
   *
   * <p>Wird asynchron aufgelöst. Solange (oder falls dauerhaft) kein Name vorliegt, zeigt die
   * Oberfläche kein Objekt-Badge – lieber gar keins als eine rohe Kennung.
   */
  contextEntityLabel?: string;

  private static readonly NON_ENTITY_SEGMENTS: readonly string[] = [
    'new', 'overview', 'create', 'choose-files', 'choose-rules', 'custom', 'loading', 'builder',
  ];

  private static readonly SURFACES: readonly ChatSurface[] = ['classic', 'mycheck', 'edit'];

  private readonly sessions: Record<ChatSurface, ChatSession> = {
    classic: new ChatSession('classic'),
    mycheck: new ChatSession('mycheck'),
    edit: new ChatSession('edit'),
  };

  /**
   * Die Oberfläche, auf die sich die Alt-Zugriffe (`currentChat`, `currentChatMessages`, …)
   * beziehen.
   *
   * <p>Bewusst **nur** dafür: Nachrichten-Routing und alle Methoden mit Seiteneffekt bekommen die
   * Oberfläche explizit. Sonst wäre die geteilte Sperre nur an eine andere Stelle gewandert.
   */
  activeSurface: ChatSurface = 'classic';

  /** Der Zustand einer Oberfläche. */
  session(surface: ChatSurface): ChatSession {
    return this.sessions[surface];
  }

  /**
   * Der Modus, in dem die aktive Oberfläche läuft.
   *
   * @deprecated Nur noch für Aufrufer außerhalb von `components/chatBIC`. Innerhalb des Chats wird
   * die Oberfläche explizit übergeben.
   */
  get chatMode(): ChatSurface {
    return this.activeSurface;
  }

  /**
   * Ist die eingebettete myCheck-Chatfläche gerade aktiv?
   *
   * <p>Deckt sich mit der Lebensdauer des Quickstarts. `AppComponent` blendet den schwebenden Chat
   * solange aus – zwei Chatflächen übereinander bedeuten zwei Eingaben auf derselben Konversation.
   */
  get isMycheckSurfaceActive(): boolean {
    return this.activeSurface === 'mycheck';
  }

  /** Betritt die eingebettete myCheck-Oberfläche (Quickstart). */
  openMycheckSurface(): void {
    this.activeSurface = 'mycheck';
  }

  /**
   * Verlässt die myCheck-Oberfläche und gibt ihren Zustand frei.
   *
   * <p>Der klassische Chat bleibt dabei stehen, wie er war – vorher ging er beim Betreten und
   * Verlassen des Quickstarts jedes Mal verloren.
   */
  /**
   * Bleibt die laufende Navigation innerhalb desselben hybriden myChecks (Set-Editor ↔ Regel-Editor)?
   * Dann soll der Edit-Chat offen bleiben: er gehört zum ganzen myCheck, und gerade im Regel-Editor
   * entstehen die Vorschläge für die Regel-Logik. Ohne laufende Navigation: nein.
   */
  navigationStaysInHybridMycheck(router: Router, rulesetId: string): boolean {
    const target = router.getCurrentNavigation()?.finalUrl?.toString() ?? '';
    return !!rulesetId && target.split('?')[0].includes(`/${rulesetId}/hybrid`);
  }

  closeMycheckSurface(): void {
    const mycheck = this.sessions.mycheck;
    this.clearBotThinkingWatchdog(mycheck);
    mycheck.currentChat = undefined;
    mycheck.messages = [];
    mycheck.allFiles = [];
    mycheck.isBotThinking = false;
    mycheck.question = '';
    mycheck.pendingUiBlockSelection = undefined;
    mycheck.messageViewComponent = undefined;
    this.clearSelectedContext('mycheck');
    this.resetMycheckQuickstartState();
    this.activeSurface = 'classic';
  }
  /**
   * Der myCheck (Prüfregelset), an dem der Quickstart gerade arbeitet.
   *
   * <p>Bewusst die Regelset-ID und nicht mehr die Prüflauf-Definition: ein myCheck ist selbst
   * ausführbar, chatBIC erzeugt ausschließlich myChecks.
   */
  activeMycheckRulesetId: string | undefined;
  /**
   * Typ des myChecks, an dem der Quickstart arbeitet: 'KI' (Dokumentprüfung) oder 'IDS'
   * (IFC-Modellprüfung).
   *
   * <p>Der Agent entscheidet ihn seit 08/2026 selbst im Interview und meldet ihn mit dem
   * `ruleset_created`-Signal zurück. Er wird gebraucht, um in den **richtigen** Editor zu springen
   * (`/ki` vs. `/ids`) und damit der Bot nach einem Reload das passende Edit-Tool bekommt – die
   * beiden Typen liegen in verschiedenen Tabellen.
   */
  activeMycheckType: 'KI' | 'IDS' | 'HYBRID' | undefined;
  /**
   * Feuert, wenn der Agent den myCheck angelegt hat – mit ID und gewähltem Typ.
   *
   * <p>Getrennt von {@link mycheckRulesetCompleted}: „angelegt" heißt, dass der Typ entschieden ist
   * und ID sowie Editor-Route jetzt bekannt sind; „fertig" heißt, dass der myCheck startbar ist.
   * Zwischen beiden liegen üblicherweise mehrere Turns.
   */
  readonly mycheckRulesetCreated = new EventEmitter<{ rulesetId: string; type: 'KI' | 'IDS' | 'HYBRID' }>();
  /**
   * Platzhalter-Schlüssel für die Phase **vor** der Typentscheidung.
   *
   * <p>Der myCheck existiert beim Chat-Start noch nicht, die gesamte Quickstart-Mechanik
   * (Thread-Zuordnung, Bootstrap-Warteschlange, Aktiv-Merker) ist aber auf eine ID als Schlüssel
   * gebaut. Dieser Platzhalter füllt die Lücke lokal und wird bei `ruleset_created` gegen die echte
   * `rulesetId` getauscht ({@link adoptCreatedMycheck}).
   *
   * <p>Er darf **nie** als `rulesetId` an den Bot gehen – der hielte ihn für einen bestehenden
   * myCheck und bekäme nur Edit-Tools statt der Create-Tools.
   */
  private pendingMycheckKey: string | undefined;
  /**
   * myCheck-Schlüssel → gewählter Typ ('KI' | 'IDS' | 'HYBRID').
   *
   * <p>Getrennt vom Aktiv-Merker, dessen Wert der angezeigte Name ist. Ohne diese Ablage wüsste das
   * Frontend nach einem Reload den Typ erst wieder mit der nächsten Bot-Antwort – der
   * Expertenmodus-Sprung landete bis dahin im falschen Editor.
   */
  private readonly mycheckQuickstartTypeStorageKey = 'mycheck-quickstart-type';
  /** Schon verarbeitete Anlage-Signale – dieselbe Entprellung wie bei den Abschluss-Signalen. */
  private readonly createdSignalsHandled = new Set<string>();

  /**
   * Resolves .frag URLs from the user's selected files.
   * Returns a JSON-encoded array of {name, url} objects (ACS metadata only supports string values).
   */
  /**
   * Die .frag-URLs, die der Bot bekommt – aus der Nutzerauswahl **und** dem offenen Viewer.
   *
   * <p>Bewusst gemergt statt `selectedFiles` zu ueberschreiben: die Kontext-Chips und die
   * Rekonstruktion in {@link getChatMessages} sind rein nutzergetrieben und bleiben es. Bei
   * gleicher URL gewinnt die Nutzerauswahl.
   */
  private resolveFragFileUrls(session: ChatSession): string {
    const fromSelection = session.selectedFiles
      .filter(f => f.fragmentsLink)
      .map(f => ({name: f.fileName, url: f.fragmentsLink!}));
    const fromViewer = this.activeViewerContext?.models ?? [];
    const entries = [...fromSelection, ...fromViewer]
      .filter((entry, index, all) => all.findIndex(other => other.url === entry.url) === index);
    return entries.length > 0 ? JSON.stringify(entries) : '';
  }

  /**
   * Zusatz-Metadata, wenn der Nutzer im 3D-Viewer sitzt.
   *
   * <p>Damit weiss der Agent, dass die Modelle **schon offen** sind, und muss nicht fragen, welches
   * gemeint ist. Die Namensliste ist begrenzt – ACS-Metadata sind Strings mit Groessenlimit.
   */
  private getViewerMetadata(): Record<string, string> {
    const viewer = this.activeViewerContext;
    if (!viewer) return {};
    const names = viewer.models.slice(0, 10).map(model => model.name);
    return {
      viewerFileId: viewer.fileId ?? '',
      viewerFileName: viewer.fileName ?? '',
      viewerModelNames: JSON.stringify(names),
      viewerIsCoordination: viewer.isCoordination ? 'true' : 'false',
    };
  }

  readonly definedPrompts = [
    'Zu wie viel Prozent wird das Budget...?',
    'Wie viele Etagen...?',
    'Gibt es Bereiche, in denen Einsparungen vorgenommen werden könnten?',
    'Wie viele Öffnungen gibt es im ersten Stock?'
  ];
  readonly floatingSuggestedPrompts = [
    'Wie kann ich meine Dokumente prüfen?',
    'Wie ist der Prüfstand in diesem Projekt?',
    'Wie kann ich eine Aufgabe erstellen?',
    'Erfüllt das BIM-Modell die Anforderungen',
  ];

  /**
   * Vorschläge passend zu dem, was gerade offen ist. Der Chat kennt den geöffneten Bericht bzw. Lauf
   * über die mitgeschickte Route – die Vorschläge zeigen, was sich damit fragen lässt.
   */
  get contextSuggestedPrompts(): string[] {
    const {entityKind, module} = this.currentPlatformContext;
    if (entityKind === 'report') {
      return [
        'Fasse die Befunde dieses Berichts zusammen',
        'Was hat sich seit der letzten Prüfung geändert?',
        'Erstelle Aufgaben für die kritischen Befunde',
      ];
    }
    if (entityKind === 'run') {
      return ['Ist die Prüfung schon fertig?', 'Warum ist die Prüfung fehlgeschlagen?'];
    }
    if (module === ChatService.VIEWER_MODULE) {
      return [
        'Zeig mir die fehlerhaften Bauteile der letzten Prüfung',
        'Welche Anforderungen verletzt das ausgewählte Bauteil?',
        'Wie viele Türen gibt es im Modell?',
      ];
    }
    if (module === 'my-checks' || module === 'check-runs' || module === 'reports') {
      return [
        'Welche myChecks gibt es in diesem Projekt?',
        'Wie lief die letzte Prüfung?',
        'Starte einen passenden myCheck für meine Dateien',
      ];
    }
    return this.floatingSuggestedPrompts;
  }
  user: any;
  projects: { id: string; name: string }[] = [];
  currentProject: number = 0;

  /**
   * Stand des chatBIC-Kontingents, auf das Chats im aktuellen Projekt zählen.
   *
   * <p>Pro Projekt und nicht aus dem State: gezählt wird gegen das Abo des Projekts (Organisation
   * vor Besitzer), derselbe Nutzer kann im einen Projekt gesperrt sein und im anderen nicht.
   * Maßgeblich bleibt das Backend – es lehnt einen Turn mit 402 ab, siehe `rejectForQuota`.
   */
  chatQuota: {
    used?: number;
    limit?: number;
    exceeded: boolean;
    resetsAt?: Date;
    scope?: 'ORGANISATION' | 'PERSONAL';
  } = {exceeded: false};

  // --- Alt-Zugriffe auf die aktive Oberfläche -------------------------------------------------
  // Sie halten Aufrufer außerhalb von components/chatBIC (ki-editor, ids-editor,
  // my-checks-builder) am Laufen, während der Chat selbst die Oberfläche explizit übergibt.
  // @deprecated jeweils: innerhalb des Chats `session(surface)` benutzen.

  get BIM_Check(): SimpleCheck | undefined {
    return this.sessions[this.activeSurface].BIM_Check;
  }

  set BIM_Check(check: SimpleCheck | undefined) {
    this.sessions[this.activeSurface].BIM_Check = check;
  }

  get LOI_Check(): SimpleCheck | undefined {
    return this.sessions[this.activeSurface].LOI_Check;
  }

  set LOI_Check(check: SimpleCheck | undefined) {
    this.sessions[this.activeSurface].LOI_Check = check;
  }

  get QUALITY_Check(): SimpleCheck | undefined {
    return this.sessions[this.activeSurface].QUALITY_Check;
  }

  set QUALITY_Check(check: SimpleCheck | undefined) {
    this.sessions[this.activeSurface].QUALITY_Check = check;
  }

  get HOAI_Check(): SimpleCheck | undefined {
    return this.sessions[this.activeSurface].HOAI_Check;
  }

  set HOAI_Check(check: SimpleCheck | undefined) {
    this.sessions[this.activeSurface].HOAI_Check = check;
  }

  get selectedFiles(): FileReference[] {
    return this.sessions[this.activeSurface].selectedFiles;
  }

  set selectedFiles(files: FileReference[]) {
    this.sessions[this.activeSurface].selectedFiles = files;
  }

  private readonly mycheckBootstrapSentKeys = new Set<string>();
  private readonly mycheckBootstrapInFlightKeys = new Set<string>();
  private readonly mycheckBootstrapQueueStorageKey = 'mycheck-bootstrap-queue';
  /** Höchstzahl der Threads, die eine Suche anschaut, wenn die Zuordnung fehlt. */
  private static readonly MYCHECK_THREAD_SCAN_LIMIT = 40;
  /**
   * myCheck-ID → ACS-Thread-ID.
   *
   * <p>Ohne diese Zuordnung musste der passende Thread über {@link listChatThreads} gesucht werden –
   * und weil die Thread-Metadata nur per {@code getProperties()} zu holen ist, war das **ein
   * HTTP-GET pro Thread**. Bei ein paar Dutzend Threads antwortet ACS mit 429 (Too Many Requests),
   * und der Quickstart startete nicht mehr.
   */
  private readonly mycheckThreadMapStorageKey = 'mycheck-thread-ids';
  /**
   * myChecks, für die der Quickstart läuft.
   *
   * <p>Nötig, weil der "Bootstrap gesendet"-Merker nur im Speicher liegt und die Warteschlange nach
   * dem Senden geleert wird: nach einem Reload wäre der Chat sonst mitten im Gespräch verschwunden
   * und der Nutzer stünde vor dem leeren Formular.
   */
  private readonly mycheckQuickstartActiveStorageKey = 'mycheck-quickstart-active';

  /** Schlüssel, unter dem der laufende Quickstart lokal geführt wird (echte ID oder Platzhalter). */
  get activeMycheckKey(): string | undefined {
    return this.activeMycheckRulesetId ?? this.pendingMycheckKey;
  }
  /** Schon gemeldete Abschluss-Signale – gegen Mehrfachauslösung über verschiedene Zustellwege. */
  private readonly completedSignalsHandled = new Set<string>();
  /** Schon gemeldete Vorschlags-Signale (Proposal-IDs) – dieselbe Entprellung wie oben. */
  private readonly proposalSignalsHandled = new Set<string>();
  /** Bereits ausgeführte Viewer-Aktionen – eine Wiederaufnahme spielt sie sonst ein zweites Mal ab. */
  private readonly viewerActionsHandled = new Set<string>();
  /**
   * Erst bei Bedarf aufgelöst: ChatService entsteht beim App-Start, der Viewer-Service soll dafür
   * nicht mitsamt seinen Engine-Abonnements vorzeitig gebaut werden.
   */
  private injector = inject(Injector);
  /** Laufende Thread-Suchen pro myCheck, damit parallele Aufrufe nicht doppelt scannen. */
  private readonly mycheckThreadLookups = new Map<string, Promise<ChatThreadEntry | undefined>>();
  /** Feuert, wenn der Quickstart einen myCheck als vollständig gemeldet hat (mit seiner ID). */
  readonly mycheckRulesetCompleted = new EventEmitter<string | undefined>();
  /**
   * Stand einer myCheck-Serie im Quickstart (mehrere myChecks in einem Gespräch, BIC-Backend#189).
   *
   * <p>Solange eine Serie läuft, beendet ein fertiger myCheck den Quickstart **nicht**: der nächste
   * Punkt startet automatisch ({@link maybeContinueMycheckSeries}).
   */
  mycheckSeries: ChatMycheckSeriesDto | undefined;
  /** Vom Nutzer angehalten – das automatische Weiter ruht, bis er fortsetzt. */
  mycheckSeriesPaused = false;
  readonly mycheckSeriesChanged = new EventEmitter<ChatMycheckSeriesDto | undefined>();
  /** Hat der letzte Turn die Serie vorangebracht? Nur dann geht es automatisch weiter – sonst drohte eine Schleife. */
  private mycheckSeriesProgressed = false;
  private mycheckSeriesTimer: number | undefined;
  /**
   * Anfang der internen Startanweisung des Quickstarts. Der Server speichert sie als gewöhnliche
   * Nutzernachricht ohne Kennzeichen – beim Laden des Verlaufs ist dieser Text das einzige Merkmal,
   * an dem sie sich ausblenden lässt.
   */
  private static readonly MYCHECK_BOOTSTRAP_PREFIX = 'Bitte starte die myCheck-Erstellung für den Namen:';
  /**
   * Ein Antwort-Stream ist zu Ende – erfolgreich oder abgebrochen. Trägt die Oberfläche, damit
   * Aufrufer auf genau ihren Turn warten können (z. B. „Übernehmen" im Quickstart).
   */
  readonly turnFinished = new EventEmitter<ChatSurface>();
  /**
   * Feuert, wenn der Agent im Edit-Modus einen **neuen** Änderungsvorschlag erzeugt hat.
   *
   * <p>„Neu" heißt: der Bot meldet das Übergangs-Flag `mycheck_proposal_created` (nur gesetzt, wenn
   * sich die `proposalId` gegenüber der letzten Antwort dieser Konversation geändert hat) – nicht
   * jede Antwort, die zufällig noch dieselbe, weiterhin PENDING `proposalId` nennt.
   */
  readonly mycheckProposalReceived = new EventEmitter<{ rulesetId: string; proposalId: string }>();

  /** True, solange der Quickstart läuft, ohne dass der myCheck schon existiert. */
  get isMycheckPending(): boolean {
    return !!this.pendingMycheckKey && !this.activeMycheckRulesetId;
  }

  /** True, wenn der Schlüssel ein Platzhalter ist (myCheck noch nicht angelegt). */
  static isPendingMycheckKey(key: string | undefined): boolean {
    return !!key?.startsWith(ChatService.PENDING_MYCHECK_PREFIX);
  }

  /** Human-readable label for the current platform module */
  getContextLabel(): string {
    const labels: Record<string, string> = {
      'dashboard': 'Dashboard',
      'files': 'Dateien',
      'tasks': 'Tasks',
      'quality': 'All-Right Check',
      'all-in-check': 'All-In Check',
      'reports': 'Berichte',
      'ifc-viewer': 'IFC-Viewer',
      'settings': 'Einstellungen',
      // Ohne diesen Eintrag zeigen Quickstart-Nachrichten kein Quellen-Badge: platformModule ist
      // nur das dritte URL-Segment, für /projects/<id>/my-checks/... also 'my-checks'.
      'my-checks': 'myChecks',
      'check-runs': 'Prüfläufe',
      [ChatService.VIEWER_MODULE]: 'IFC-Viewer',
    };
    return labels[this.currentPlatformContext.module] || '';
  }

  async bootstrapMycheckForNewCheck(rulesetId: string, checkName: string | null | undefined): Promise<void> {
    const normalizedCheckName = (checkName ?? '').trim();
    if (!rulesetId || !normalizedCheckName) return;

    const projectId = this.stateConfigService.getProjectId();
    if (!projectId) return;

    const bootstrapKey = `${projectId}:${rulesetId}`;
    if (this.mycheckBootstrapSentKeys.has(bootstrapKey) || this.mycheckBootstrapInFlightKeys.has(bootstrapKey)) {
      return;
    }

    this.mycheckBootstrapInFlightKeys.add(bootstrapKey);
    try {
      const chatReady = await this.ensureChatReadyForProject(projectId);
      if (!chatReady) return;

      this.openMycheckSurface();
      // Beim typoffenen Start ist `rulesetId` der Platzhalter `pending-…`. Als echte ID behandelt,
      // landete er über createChat als ruleset_id am Thread – der Agent las daraus „Regelwerk
      // existiert bereits" und rief nie ein Create-Werkzeug auf.
      const isPending = rulesetId.startsWith(ChatService.PENDING_MYCHECK_PREFIX);
      await this.ensureMycheckThread(rulesetId, isPending);
      const chatThreadId = this.sessions.mycheck.currentChat?.id;
      if (!chatThreadId) return;

      await this.sendMycheckBootstrapActivity(chatThreadId, normalizedCheckName);
      this.mycheckBootstrapSentKeys.add(bootstrapKey);
    } catch (error) {
      console.error('Error bootstrapping mycheck chat:', error);
    } finally {
      this.mycheckBootstrapInFlightKeys.delete(bootstrapKey);
    }
  }
  chatHistoryExpanded = false;
  allFilesExpanded = false;
  threads: { id: string; topic: string; createdOn: Date }[] = [];
  /**
   * Höchstwartezeit auf eine Bot-Antwort, danach wird die Eingabe wieder freigegeben.
   *
   * <p>Orientiert sich an den serverseitigen Grenzen des Bots (`AGENT_RESPONSE_TIMEOUT_SECONDS`
   * = 25 s klassisch, `QUICKSTART_TIMEOUT_SECONDS` = 300 s im Quickstart) plus Reserve für die
   * Zustellung über ACS.
   */
  private static readonly BOT_WATCHDOG_CLASSIC_MS = 60_000;
  private static readonly BOT_WATCHDOG_MYCHECK_MS = 330_000;
  chatIdFromRouting: string | undefined;

  get allFiles(): ProjectFile[] {
    return this.sessions[this.activeSurface].allFiles;
  }

  set allFiles(files: ProjectFile[]) {
    this.sessions[this.activeSurface].allFiles = files;
  }

  get question(): string {
    return this.sessions[this.activeSurface].question;
  }

  set question(value: string) {
    this.sessions[this.activeSurface].question = value;
  }

  get currentChatMessages(): Message[] {
    return this.sessions[this.activeSurface].messages;
  }

  set currentChatMessages(messages: Message[]) {
    this.sessions[this.activeSurface].messages = messages;
  }

  /** Der gerade geöffnete Chat-Thread der aktiven Oberfläche. */
  get currentChat(): ChatThreadEntry | undefined {
    return this.sessions[this.activeSurface].currentChat;
  }

  set currentChat(chat: ChatThreadEntry | undefined) {
    this.setCurrentChat(this.activeSurface, chat);
  }
  private chatApiService = inject(ChatApiService);
  private chatContextLabelService = inject(ChatContextLabelService);

  /**
   * True, solange auf eine Bot-Antwort gewartet wird – sperrt Senden-Button und Enter-Taste
   * (`message-view.component.html`).
   */
  get isBotThinking(): boolean {
    return this.sessions[this.activeSurface].isBotThinking;
  }

  set isBotThinking(value: boolean) {
    this.setBotThinking(this.sessions[this.activeSurface], value);
  }

  /**
   * Setzt die Denk-Sperre **einer** Oberfläche und zieht den Watchdog nach.
   *
   * <p>Zurückgesetzt wurde die Sperre bisher ausschließlich durch eine eintreffende Bot-Nachricht.
   * Blieb die aus – Timeout im Agenten, Fehler vor dem Posten der Antwort, verlorene
   * Realtime-Benachrichtigung – war der Chat bis zum Reload unbedienbar, ohne dass der Nutzer den
   * Grund sehen konnte. Deshalb die Notbremse.
   *
   * <p>Die Grenze kommt aus `session.surface`, nicht aus einem globalen Modus: eine klassische
   * Nachricht, die während eines laufenden Quickstarts abging, bekam vorher dessen 330-s-Limit.
   */
  setBotThinking(session: ChatSession, value: boolean): void {
    session.isBotThinking = value;
    this.clearBotThinkingWatchdog(session);
    if (!value) return;

    // Im Quickstart und im Edit-Modus darf der Agent deutlich länger arbeiten (Tool-Aufrufe gegen
    // planBIC bzw. gegen die myCheck-/Prüfergebnis-Tools).
    const limitMs = session.surface === 'mycheck'
      ? ChatService.BOT_WATCHDOG_MYCHECK_MS
      : session.surface === 'edit'
        ? ChatService.BOT_WATCHDOG_EDIT_MS
        : ChatService.BOT_WATCHDOG_CLASSIC_MS;
    session.botThinkingWatchdog = window.setTimeout(() => {
      session.botThinkingWatchdog = undefined;
      if (!session.isBotThinking) return;
      session.isBotThinking = false;
      this.addBotTimeoutEventMessage(session);
    }, limitMs);
  }

  private clearBotThinkingWatchdog(session: ChatSession): void {
    if (session.botThinkingWatchdog !== undefined) {
      window.clearTimeout(session.botThinkingWatchdog);
      session.botThinkingWatchdog = undefined;
    }
  }

  /**
   * Scroll-Signal der aktiven Oberfläche.
   *
   * @deprecated Ansichten abonnieren `session(surface).messagesScrollDown`, sonst scrollen zwei
   * gemountete Ansichten gemeinsam.
   */
  get messagesScrollDown(): EventEmitter<void> {
    return this.sessions[this.activeSurface].messagesScrollDown;
  }

  queueMycheckBootstrapForNewCheck(rulesetId: string, checkName: string | null | undefined): void {
    const normalizedCheckName = (checkName ?? '').trim();
    if (!rulesetId || !normalizedCheckName) return;
    const projectId = this.stateConfigService.getProjectId();
    if (!projectId) return;

    const queue = this.readMycheckBootstrapQueue();
    queue[this.getMycheckBootstrapKey(projectId, rulesetId)] = normalizedCheckName;
    this.writeMycheckBootstrapQueue(queue);
  }
  private hoaiCheckControllerService = inject(HoaiCheckControllerService);
  private hasLoadedThreads = false;
  /** Seitenzähler der Chatliste. Ersetzt den früheren Zähler gescannter ACS-Threads. */
  private threadsPage = 0;
  private readonly threadsPerPage = 20;
  private allThreadsLoaded = false;
  private token = '';

  hasQueuedMycheckBootstrap(rulesetId: string): boolean {
    const projectId = this.stateConfigService.getProjectId();
    if (!projectId || !rulesetId) return false;
    const queue = this.readMycheckBootstrapQueue();
    return !!queue[this.getMycheckBootstrapKey(projectId, rulesetId)];
  }

  hasMycheckBootstrapState(rulesetId: string): boolean {
    const projectId = this.stateConfigService.getProjectId();
    if (!projectId || !rulesetId) return false;

    const bootstrapKey = this.getMycheckBootstrapKey(projectId, rulesetId);
    if (this.mycheckBootstrapSentKeys.has(bootstrapKey)) return true;
    if (this.mycheckBootstrapInFlightKeys.has(bootstrapKey)) return true;
    return this.hasQueuedMycheckBootstrap(rulesetId);
  }

  /**
   * Verschickt einen vorgemerkten Bootstrap, falls einer für diesen myCheck ansteht.
   *
   * <p>Unterscheidet bewusst 'sent' von 'empty': ein Aufrufer, der beide Fälle als "noch nicht
   * fertig" behandelt, wiederholt sonst endlos, obwohl gar nichts vorgemerkt ist.
   */
  async flushQueuedMycheckBootstrap(rulesetId: string): Promise<'sent' | 'empty' | 'not-ready'> {
    const projectId = this.stateConfigService.getProjectId();
    if (!projectId || !rulesetId) return 'not-ready';

    const queue = this.readMycheckBootstrapQueue();
    const bootstrapKey = this.getMycheckBootstrapKey(projectId, rulesetId);
    const queuedCheckName = queue[bootstrapKey];
    if (!queuedCheckName) return 'empty';

    await this.bootstrapMycheckForNewCheck(rulesetId, queuedCheckName);
    if (!this.mycheckBootstrapSentKeys.has(bootstrapKey)) return 'not-ready';

    delete queue[bootstrapKey];
    this.writeMycheckBootstrapQueue(queue);
    return 'sent';
  }

  /**
   * Merkt die Eingabe-Komponente einer Oberfläche (für den Ordner-Dialog).
   *
   * <p>Pro Oberfläche, weil beide Ansichten gleichzeitig gemountet sein können: vorher gewann die
   * zuletzt konstruierte Instanz, und der Dialog öffnete sich in einer abgehängten Komponente.
   */
  setMessageViewComponent(surface: ChatSurface, component: MessageViewComponent) {
    this.sessions[surface].messageViewComponent = component;
  }

  /** Gibt die Registrierung wieder frei, wenn die Ansicht verschwindet. */
  clearMessageViewComponent(surface: ChatSurface, component: MessageViewComponent) {
    if (this.sessions[surface].messageViewComponent === component) {
      this.sessions[surface].messageViewComponent = undefined;
    }
  }

  /**
   * Setzt den offenen Thread einer Oberfläche.
   *
   * <p>Beim Wechsel wird die Eingabe wieder freigegeben: ein ausstehendes {@link isBotThinking}
   * gehört zum **vorherigen** Thread, und aufgehoben wird es ausschließlich durch eine Nachricht in
   * genau diesem Thread. Wer einen neuen Chat
   * öffnete, während noch eine Antwort ausstand, konnte dort sonst nie wieder senden.
   */
  setCurrentChat(surface: ChatSurface, chat: ChatThreadEntry | undefined): void {
    const session = this.sessions[surface];
    const changed = session.currentChat?.id !== chat?.id;
    session.currentChat = chat;
    if (changed) {
      this.setBotThinking(session, false);
    }
  }

  openContextSelector(surface: ChatSurface): void {
    this.triggerFolderPopup(surface);
  }

  private triggerFolderPopup(surface: ChatSurface) {
    this.sessions[surface].messageViewComponent?.onPlusButtonClick();
  }

  getUser() {
    return this.user;
  }

  getProjects() {
    return this.projects;
  }

  renameTopicTo(surface: ChatSurface, newTopic: string) {
    const currentChat = this.sessions[surface].currentChat;
    if (currentChat && newTopic.trim().length > 0) {
      this.changeTopic(currentChat.id, newTopic);
    }
  }

  /**
   * Leert **eine** Oberfläche für einen neuen Chat.
   *
   * <p>Der Modus wandert nicht mehr mit: welcher Thread entsteht, entscheidet die übergebene
   * Oberfläche. "Neuer Chat" im schwebenden Chat legte vorher während eines laufenden Quickstarts
   * einen Thread mit `mode:'mycheck'` und `rulesetId` an und nahm dem Quickstart sein `currentChat`.
   */
  resetChatState(surface: ChatSurface): void {
    const session = this.sessions[surface];
    this.setCurrentChat(surface, undefined);
    session.messages = [];
    session.allFiles = [];
    this.setBotThinking(session, false);
    this.clearSelectedContext(surface);

    // Der Prüfungs-Kontext aus der URL gehört zum klassischen Chat; die myCheck-Fläche hängt am
    // myCheck, nicht an der gerade geöffneten Prüfungsseite.
    if (surface !== 'classic') return;
    // Re-apply check context from current URL (e.g. user starts a new chat while still on a check page)
    const {checkId, checkType, projectId} = this.currentPlatformContext;
    if (checkId && checkType && projectId) {
      this.autoSetCheckFromContext(checkId, checkType, projectId);
    }
  }

  isChatReady(): boolean {
    // Der Chat laeuft ueber die normale API: sobald ein Projekt bekannt ist, ist er bereit.
    // Es gibt keinen eigenen Chat-Client und kein zweites Token mehr, auf die man warten muesste.
    return this.isInitialized;
  }

  /**
   * Stellt eine Frage und schreibt die Antwort mit, während sie entsteht.
   *
   * <p>Ersetzt den bisherigen Weg über Azure Communication Services. Dort wurde die Nachricht in
   * einen Chat-Thread geschrieben, und die Antwort kam irgendwann als **eine** fertige Nachricht
   * zurück – ACS-Chatnachrichten sind atomar, Token-Streaming war dort nicht möglich.
   *
   * <p>Zwei Dinge fallen damit ersatzlos weg: das planBIC-JWT, das bisher in der Metadata **jeder**
   * Nachricht mitreiste und dauerhaft im Chatspeicher liegen blieb, und die `projectId` als
   * Client-Angabe – beides steht jetzt am authentifizierten Request bzw. am Thread.
   */
  async sendMessage(
    surface: ChatSurface,
    chatThreadId: string,
    message: string,
    additionalMetadata?: Record<string, string>,
  ) {
    const session = this.sessions[surface];
    this.setBotThinking(session, true);
    session.toolSteps = [];

    const clientMessageId = crypto.randomUUID();

    const selectedFiles = [...session.selectedFiles];
    const context = {
      projectFileIds: selectedFiles.map(f => f.id),
      hoaiCheckId: session.HOAI_Check?.id ?? null,
      bimCheckId: session.BIM_Check?.id ?? null,
      loiCheckId: session.LOI_Check?.id ?? null,
      allRightCheckId: session.QUALITY_Check?.id ?? null,
      platformRoute: this.currentPlatformContext.route,
      platformModule: this.currentPlatformContext.module,
      checkRuns: [...session.selectedRuns],
      // Was auf der Seite markiert ist (Dateien im Dateimanager, Regel im Bericht) – nur im
      // klassischen Chat; Quickstart und Editor arbeiten an einem myCheck, nicht an der Seite.
      page: surface === 'classic' ? this.pageContext.snapshot(this.currentPlatformContext.route) ?? null : null,
    };

    // Optimistisch anzeigen: die Frage steht sofort im Verlauf, nicht erst nach der Serverrunde.
    // Die Metadata entscheidet mit, ob sie überhaupt erscheint (`isBootstrap` blendet die interne
    // Startanweisung des Quickstarts aus, siehe addMessageToChat) – und trägt den Kontext, aus dem
    // die Quellen-Badges unter der Antwort entstehen.
    this.addMessageToChat(surface, this.createMessage({
      id: `local-${clientMessageId}`,
      message,
      createdOn: new Date(),
      metadata: {
        ...ChatService.contextMetadata(context, selectedFiles),
        ...(additionalMetadata ?? {}),
      },
      sender: 'user',
    }), false);

    // Bewusst `addMessage` und NICHT `addMessageToChat`: letzteres baut aus den Feldern eine KOPIE
    // und haengt die in die Liste. Die Antwort wird hier aber waehrend des Streams in place
    // fortgeschrieben – mit einer Kopie in der Ansicht mutierte man ein Objekt, das niemand
    // rendert, und es erschiene nie eine Antwort.
    const streaming = this.createMessage({
      id: `streaming-${clientMessageId}`,
      message: '',
      createdOn: new Date(),
      sender: 'bot',
    });
    this.addMessage(session, streaming, false);
    session.streamingMessage = streaming;

    // Auch im myCheck-Assistenten: beim Anlegen eines IDS-myChecks liest er Klassen und Psets des offenen
    // Modells (nur lesend). Der Editor arbeitet ohne Modell.
    const viewer = surface !== 'edit' ? await this.viewerSnapshotForTurn() : undefined;

    const body = {
      clientMessageId,
      text: message,
      // Der Modus gehoert an die Nachricht, nicht nur an den Verlauf: ein myCheck hat genau EINEN
      // Verlauf, und derselbe wird im Quickstart und im Editor benutzt. Ohne diese Angabe bekaeme
      // der Editor die Schreibwerkzeuge des Quickstarts statt der Vorschlagswerkzeuge - und es
      // entstuende nie ein Aenderungsvorschlag.
      mode: surface === 'classic' ? 'CLASSIC' : surface === 'edit' ? 'EDIT' : 'MYCHECK',
      context: {
        ...context,
        uiBlockSelection: this.getUiBlockSelection(session),
        viewer: viewer ?? null,
      }
    };

    this.clearPendingUiBlockSelection(surface);

    session.streamSubscription?.unsubscribe();
    session.streamSubscription = this.chatStreamService.ask(chatThreadId, body).subscribe({
      next: event => this.applyStreamEvent(surface, streaming, event),
      error: err => {
        // In diesem Verlauf laeuft schon eine Antwort (zweiter Tab, oder der vorherige Turn ist
        // noch nicht fertig). Die Frage wurde NICHT gesendet - deshalb wird sie zurueckgegeben und
        // die Oberflaeche haengt sich an die laufende Antwort an, statt einen Fehler zu zeigen.
        if (err?.code === 'turn_running' && err?.turnId) {
          this.attachToRunningTurn(surface, streaming, message, err.turnId);
          return;
        }
        if (err?.code === 'chat_quota_exceeded') {
          this.rejectForQuota(surface, streaming, message, err.quota);
          return;
        }

        console.error('Chat-Stream fehlgeschlagen:', err);
        session.streamingMessage = undefined;
        streaming.message = streaming.message
          ? streaming.message + '\n\n_Die Verbindung wurde unterbrochen._'
          : 'Die Verbindung zum Assistenten wurde unterbrochen. Bitte versuche es erneut.';
        this.setBotThinking(session, false);
        session.toolSteps = [];
        this.turnFinished.emit(surface);
      },
      complete: () => {
        this.setBotThinking(session, false);
        session.toolSteps = [];
        session.streamingMessage = undefined;
        this.turnFinished.emit(surface);
        // Gebucht wird am Ende des Turns – danach kann das Kontingent erschöpft sein.
        this.refreshChatQuota();
        if (surface === 'mycheck') {
          this.maybeContinueMycheckSeries();
        }
      }
    });
  }

  /** Ob auf dieser Oberfläche gerade eine Antwort läuft, die sich stoppen lässt. */
  canStop(surface: ChatSurface): boolean {
    return !!this.sessions[surface].currentTurnId;
  }

  /**
   * Stopp-Knopf. Der Turn läuft auf dem Server weiter, bis er den Wunsch sieht (spätestens nach
   * ~1,5 s); er endet dann mit ABORTED/cancelled, und der Teiltext bleibt stehen.
   */
  async stopAnswer(surface: ChatSurface): Promise<void> {
    const turnId = this.sessions[surface].currentTurnId;
    if (!turnId) return;
    await this.chatApiService.cancelTurn(turnId);
  }

  /**
   * „Neu generieren": ersetzt die letzte Antwort durch eine neue auf dieselbe Frage. Die alte
   * verschwindet sofort aus der Ansicht – auf dem Server ist sie SUPERSEDED.
   */
  regenerate(surface: ChatSurface, botMessage: Message): void {
    const session = this.sessions[surface];
    const threadId = session.currentChat?.id;
    if (!threadId || !botMessage.id || botMessage.id.startsWith('streaming-') || session.isBotThinking) return;

    const index = session.messages.indexOf(botMessage);
    const streaming = this.createMessage({id: `streaming-regen-${Date.now()}`, message: '', createdOn: new Date(), sender: 'bot'});
    if (index >= 0) {
      session.messages.splice(index, 1, streaming);
    } else {
      this.addMessage(session, streaming, false);
    }
    session.streamingMessage = streaming;
    this.setBotThinking(session, true);
    session.toolSteps = [];

    const mode = surface === 'classic' ? 'CLASSIC' : surface === 'edit' ? 'EDIT' : 'MYCHECK';
    session.streamSubscription?.unsubscribe();
    session.streamSubscription = this.chatStreamService.regenerate(threadId, botMessage.id, mode).subscribe({
      next: event => this.applyStreamEvent(surface, streaming, event),
      error: err => {
        console.error('Neu generieren fehlgeschlagen:', err);
        // Die alte Antwort ist auf dem Server nur dann ersetzt, wenn der Turn begonnen hat – sonst
        // gilt sie weiter und kommt zurück in die Ansicht.
        if (!session.currentTurnId && index >= 0) {
          session.messages.splice(session.messages.indexOf(streaming), 1, botMessage);
        } else {
          streaming.message = 'Die Antwort konnte nicht neu erzeugt werden. Bitte versuche es erneut.';
        }
        session.streamingMessage = undefined;
        this.setBotThinking(session, false);
        session.toolSteps = [];
      },
      complete: () => {
        this.setBotThinking(session, false);
        session.toolSteps = [];
        session.streamingMessage = undefined;
        this.turnFinished.emit(surface);
        this.refreshChatQuota();
      },
    });
  }

  /** Daumen an einer Antwort. Ein zweiter Klick auf denselben Daumen nimmt die Bewertung zurück. */
  async setFeedback(surface: ChatSurface, botMessage: Message, rating: 1 | -1): Promise<void> {
    const threadId = this.sessions[surface].currentChat?.id;
    if (!threadId || !botMessage.id || botMessage.id.startsWith('streaming-')) return;
    const current = botMessage.metadata?.['feedbackRating'];
    const next = current === String(rating) ? null : rating;
    const dto = await this.chatApiService.setFeedback(threadId, botMessage.id, next);
    botMessage.metadata = {...(botMessage.metadata ?? {}), feedbackRating: dto.feedbackRating != null ? String(dto.feedbackRating) : ''};
  }

  /**
   * Lädt den Kontingentstand des aktuellen Projekts neu.
   *
   * <p>Ein Fehler hier sperrt nichts: die Anzeige bleibt beim letzten Stand, und das Backend prüft
   * beim Senden ohnehin selbst.
   */
  refreshChatQuota(): void {
    const projectId = this.currentProject ? Number(this.currentProject) : undefined;
    if (!projectId) {
      this.chatQuota = {exceeded: false};
      return;
    }
    this.chatApiService.quota(projectId)
      .then(quota => {
        // Antwort zu einem inzwischen verlassenen Projekt verwerfen.
        if (Number(this.currentProject) === projectId) {
          this.applyChatQuota(quota);
        }
      })
      .catch(error => console.error('chatBIC-Kontingent konnte nicht geladen werden:', error));
  }

  handleProjectChange(newProjectId: number): void {
    // ALLE Oberflächen: der myCheck- bzw. Edit-Chat des alten Projekts darf nicht ins neue
    // hinüberreichen.
    ChatService.SURFACES.forEach(surface => this.resetChatState(surface));
    this.activeSurface = 'classic';
    // Projektwechsel beendet einen laufenden Quickstart mit: Typ und Platzhalter gehören zum alten
    // Projekt und würden sonst in die Metadata des neuen wandern. Dasselbe gilt für die Modell-URL
    // eines im alten Projekt geöffneten Viewers.
    this.resetMycheckQuickstartState();
    this.activeViewerContext = undefined;
    this.threads = [];
    this.hasLoadedThreads = false;
    this.threadsPage = 0;
    this.allThreadsLoaded = false;
    this.currentProject = newProjectId;
    // Das Kontingent gehört zum alten Projekt; der Stand des neuen kommt mit initializeChat().
    this.chatQuota = {exceeded: false};
    this.isInitialized = false;
    this.chatIdFromRouting = undefined;
    this.initializeChat();
  }

  /**
   * Bereitet den Chat vor.
   *
   * <p>Deutlich kürzer als zuvor: es gibt keine Chat-Identität und kein zweites Token mehr. Der
   * Chat läuft über die normale API mit dem vorhandenen planBIC-Token – die Schritte „Identität
   * anlegen", „Chat-Token holen", „Token zwischenspeichern" und „Client aufbauen" entfallen
   * ersatzlos, und mit ihnen die Fehlerquelle eines zweiten, eigenständig ablaufenden Tokens.
   */
  initializeChat(): void {
    if (this.isInitialized) return;
    this.isInitialized = true;
    this.startRouteTracking();

    this.authControllerService.profile().pipe(
      switchMap(user => {
        this.user = user;
        this.currentProject = this.stateConfigService.getProjectId();
        this.initializeUserProjects();
        this.refreshChatQuota();

        // Die Route /projects/:id/chat/:chatId gehört zum klassischen Chat.
        if (!this.sessions.classic.currentChat && this.chatIdFromRouting) {
          return from(this.restoreThreadFromRouting());
        }
        return of(null);
      }),
      catchError(error => {
        console.error('Error initializing chatBIC:', error);
        return of(null);
      })
    ).subscribe();
  }

  /**
   * Hängt sich an eine bereits laufende Antwort an.
   *
   * <p>Der Kern ist nicht der Stream, sondern die Ehrlichkeit: die getippte Frage wurde
   * <strong>nicht</strong> gesendet. Sie wandert deshalb zurück ins Eingabefeld, die optimistisch
   * angezeigte Nutzernachricht verschwindet wieder, und ein Hinweis sagt, was stattdessen zu sehen
   * ist. Ohne das stünde die Frage im Verlauf, ohne je gestellt worden zu sein – und die Antwort
   * darunter gehörte zu einer anderen.
   *
   * <p>Token aus der Zeit vor dem Anhängen fehlen (sie werden serverseitig nicht protokolliert);
   * der vollständige Text kommt mit dem `done`-Ereignis.
   */
  private attachToRunningTurn(surface: ChatSurface, streaming: Message,
                              unsentQuestion: string, turnId: string): void {
    const session = this.sessions[surface];

    // Die nicht gesendete Frage zurueckgeben: optimistische Nutzernachricht entfernen, Text zurueck
    // ins Eingabefeld.
    const userIndex = session.messages.findIndex(
      m => m.sender === 'user' && m.message === unsentQuestion && m.id.startsWith('local-'));
    if (userIndex >= 0) {
      session.messages.splice(userIndex, 1);
    }
    if (!session.question?.trim()) {
      session.question = unsentQuestion;
    }

    // Als eigene Systemnachricht und NICHT in die Antwortblase: deren Text wird beim `done`-
    // Ereignis durch die vollstaendige Antwort ersetzt, der Hinweis waere dann wieder weg.
    this.addMessageToChat(surface, {
      id: `turn-running-${turnId}`,
      message: 'In diesem Chat lief noch eine Antwort – sie wird jetzt angezeigt. '
        + 'Deine Frage wurde nicht gesendet und steht wieder im Eingabefeld.',
      createdOn: new Date(),
      metadata: {eventType: 'turn_running', mode: surface},
      sender: 'system',
      uiBlock: undefined,
      files: undefined,
      table: undefined,
    }, false);

    // Der Hinweis gehoert VOR die Antwort. Die Antwortblase haengt schon in der Liste, also wird sie
    // ans Ende geschoben - bewusst durch Verschieben derselben Instanz und nicht durch eine Kopie:
    // der Stream schreibt genau dieses Objekt fort.
    const streamingIndex = session.messages.indexOf(streaming);
    if (streamingIndex >= 0) {
      session.messages.splice(streamingIndex, 1);
      session.messages.push(streaming);
    }

    session.streamSubscription?.unsubscribe();
    session.streamSubscription = this.chatStreamService.follow(turnId).subscribe({
      next: event => this.applyStreamEvent(surface, streaming, event),
      error: () => {
        session.streamingMessage = undefined;
        this.setBotThinking(session, false);
        session.toolSteps = [];
        this.turnFinished.emit(surface);
      },
      complete: () => {
        this.setBotThinking(session, false);
        session.toolSteps = [];
        session.streamingMessage = undefined;
        this.turnFinished.emit(surface);
      }
    });
  }

  /**
   * Die Metadata einer Nutzernachricht, aus der die Quellen-Badges unter der Antwort entstehen
   * (`ChatViewComponent#getMessageSources`).
   *
   * <p>Die Badges lasen diese Felder schon immer – gesetzt wurden sie seit dem Wechsel von ACS auf
   * den Backend-Stream aber nirgends mehr, weder beim Senden noch beim Laden des Verlaufs. Quelle
   * ist jetzt in beiden Fällen der gesendete Kontext; im Verlauf kommt er als `contextJson`.
   * Dateinamen kennt nur die laufende Sitzung, im Verlauf stehen nur die Kennungen.
   */
  static contextMetadata(
    context: {
      projectFileIds?: string[] | null; hoaiCheckId?: string | null; bimCheckId?: string | null;
      loiCheckId?: string | null; allRightCheckId?: string | null; platformModule?: string | null;
      checkRuns?: { name?: string }[] | null;
    } | undefined | null,
    files: { id?: string; fileName?: string }[] = [],
  ): Record<string, string> | undefined {
    if (!context) return undefined;
    const metadata: Record<string, string> = {};
    if (context.platformModule) metadata['platformModule'] = context.platformModule;
    const runNames = (context.checkRuns ?? []).map(run => run?.name).filter((name): name is string => !!name);
    if (runNames.length > 0) metadata['checkRunNames'] = JSON.stringify(runNames);
    if (context.hoaiCheckId) metadata['hoaiCheckId'] = context.hoaiCheckId;
    if (context.bimCheckId) metadata['bimCheckId'] = context.bimCheckId;
    if (context.loiCheckId) metadata['loiCheckId'] = context.loiCheckId;
    if (context.allRightCheckId) metadata['allRightCheckId'] = context.allRightCheckId;
    const fileIds = (context.projectFileIds ?? []).filter(id => !!id);
    if (fileIds.length > 0) {
      metadata['projectFileId'] = fileIds.join(',');
      const named = files.filter(f => f.id && f.fileName && fileIds.includes(f.id));
      if (named.length > 0) {
        metadata['projectFiles'] = JSON.stringify(named.map(f => ({id: f.id, fileName: f.fileName})));
      }
    }
    return Object.keys(metadata).length > 0 ? metadata : undefined;
  }

  /**
   * Lädt den Verlauf eines Threads.
   *
   * <p>Der ausgewählte Kontext einer Nachricht (Dateien, Checks) kommt jetzt als gespeichertes Feld
   * mit. Bisher musste die Oberfläche ihn rekonstruieren: sie suchte die letzte Nutzernachricht und
   * feuerte fünf Controller-Aufrufe ab, um Datei- und Check-Kontext zurückzuholen.
   */
  async getChatMessages(surface: ChatSurface, chatThreadId: string): Promise<Message[]> {
    const session = this.sessions[surface];
    const isActiveChat = () => session.currentChat?.id === chatThreadId;
    if (!isActiveChat()) {
      return session.messages;
    }

    session.messages = [];
    const page = await this.chatApiService.messages(chatThreadId);

    // Der Server liefert neueste zuerst; die Ansicht rendert chronologisch.
    for (const dto of [...page].reverse()) {
      if (!isActiveChat()) {
        return session.messages;
      }
      if (dto.role === 'SYSTEM_EVENT') {
        continue;
      }
      const isBootstrap = dto.role === 'USER'
        && (dto.content ?? '').startsWith(ChatService.MYCHECK_BOOTSTRAP_PREFIX);
      this.addMessageToChat(surface, this.createMessage({
        id: dto.id,
        message: dto.content ?? '',
        createdOn: new Date(dto.createdAt),
        metadata: isBootstrap
          ? {isBootstrap: 'true'}
          : dto.role === 'USER'
            ? ChatService.contextMetadata(this.parseJsonSafe(dto.contextJson))
            : dto.feedbackRating != null ? {feedbackRating: String(dto.feedbackRating)} : undefined,
        sender: dto.role === 'USER' ? 'user' : 'bot',
        uiBlock: this.parseJsonSafe(dto.uiBlockJson),
      }), false);
    }

    if (!isActiveChat()) {
      return session.messages;
    }

    session.messagesScrollDown.emit();
    return session.messages;
  }

  private applyChatQuota(quota: ChatQuotaDto | undefined): void {
    this.chatQuota = {
      used: quota?.used ?? undefined,
      limit: quota?.limit ?? undefined,
      exceeded: quota?.exceeded === true,
      resetsAt: quota?.resetsAt ? new Date(quota.resetsAt) : undefined,
      scope: quota?.scope ?? undefined,
    };
  }

  updateCurrentChat(surface: ChatSurface, chat: ChatThreadEntry | undefined) {
    this.setCurrentChat(surface, chat);
  }

  /**
   * Die Chatliste, seitenweise.
   *
   * <p>Eine Abfrage statt 1+N: der bisherige Weg listete alle Threads auf und holte dann **je
   * Thread** dessen Eigenschaften, nur um nach Projekt und Modus zu filtern – die dokumentierte
   * Ursache der 429-Fehler. Der zugehörige Zwischenspeicher samt Wiederholungslogik entfällt damit.
   */
  async listChats(loadMore: boolean = false): Promise<{ id: string; topic: string; createdOn: Date }[]> {
    if (!loadMore) {
      this.threads = [];
      this.threadsPage = 0;
      this.allThreadsLoaded = false;
    }

    const projectId = this.currentProject ? Number(this.currentProject) : undefined;
    const page = await this.chatApiService.listThreads(projectId, 'CLASSIC', this.threadsPage, this.threadsPerPage);

    for (const thread of page) {
      if (this.threads.some(t => t.id === thread.id)) continue;
      this.threads.push({
        id: thread.id,
        topic: thread.topic,
        createdOn: new Date(thread.lastMessageAt ?? thread.createdAt),
      });
    }

    if (page.length < this.threadsPerPage) {
      this.allThreadsLoaded = true;
    } else {
      this.threadsPage++;
    }

    this.threads.sort((a, b) => b.createdOn.getTime() - a.createdOn.getTime());
    return this.threads;
  }

  async stopListeningForNotifications() {
    // ALLE Flaechen abraeumen – seit dem Edit-Modus sind es drei. Watchdogs liefen sonst gegen
    // einen Chat weiter, der nicht mehr zuhoert, und melden einen Timeout, den niemand mehr sieht.
    // Offene Antwort-Streams werden abbestellt: der Turn laeuft serverseitig weiter und schreibt
    // sein Ergebnis fest – beendet wird nur die Verbindung, nicht die Arbeit.
    for (const surface of ChatService.SURFACES) {
      const session = this.sessions[surface];
      this.clearBotThinkingWatchdog(session);
      session.streamSubscription?.unsubscribe();
    }
  }

  addMessageToChat(surface: ChatSurface, chatMessage: Message, unshift: boolean = true) {
    const session = this.sessions[surface];
    const message: Message = this.createMessage({
      id: chatMessage.id,
      message: chatMessage.message,
      createdOn: chatMessage.createdOn,
      metadata: chatMessage.metadata,
      sender: chatMessage.sender,
      uiBlock: chatMessage.uiBlock,
    });

    if (!session.messages.some(m => m.id == message.id)) {
      if (message.sender === 'user' && message.metadata?.['isBootstrap'] === 'true') {
        return;
      }

      // Die frühere Auswertung der Nachrichten-Metadata entfällt hier vollständig.
      //
      // Nur auf der myCheck-Fläche: ein klassischer Thread, der aus irgendeinem Grund eine
      // rulesetId mitführt, darf keinen adoptCreatedMycheck-Lauf auslösen.
      if (message.sender !== 'user' && surface === 'mycheck') {
        this.handleRealtimeBotMetadata(message.metadata);
      }

      if (message.sender != 'user' && message.message && message.metadata && message.metadata["hasTable"] && message.metadata["hasTable"] == "true") {
        const messageContent = JSON.parse(message.message)
        message.message = messageContent.message
        message.table = messageContent.table
      }

      if (message.metadata && message.metadata["files"]) {
        let messageFiles: FileReference[] = []
        const filesData = JSON.parse(message.metadata["files"]);
        filesData.forEach((file: FileReference) => {
          messageFiles.push(file)
          if (session.allFiles.find(f => f.id == file.id) == undefined) {
            session.allFiles.push(file);
          }
        });
        message.files = messageFiles
      }

      const uiBlockData = message.metadata?.['uiBlock']
        ?? this.extractUiBlockFromChannelData(message.metadata?.['channelData'])
        ?? this.extractUiBlockFromChannelData(message.metadata?.['channel_data']);
      if (uiBlockData) {
        message.uiBlock = this.parseUiBlock(uiBlockData);
      }

    }

    this.addMessage(session, message, unshift)
  }

  /**
   * Legt einen Verlauf an.
   *
   * <p>Der Server antwortet **synchron** mit dem fertigen Thread. Das ersetzt die frühere
   * Bootstrap-Warteschlange im `sessionStorage`: ACS-Threads entstanden asynchron, und die
   * Oberfläche erfuhr die `rulesetId` erst aus einer späteren Bot-Antwort – weshalb sie sich
   * ausstehende Anlagen merken musste.
   */
  async createChat(surface: ChatSurface, shouldNavigate: boolean = false) {
    this.clearSelectedContext(surface);

    // Der Edit-Modus arbeitet an einem bestehenden Regelwerk und legt fachlich denselben
    // myCheck-gebundenen Verlauf an wie der Quickstart.
    const mode: ApiChatThreadMode = surface === 'classic' ? 'CLASSIC'
      : surface === 'edit' ? 'EDIT' : 'MYCHECK';
    const projectId = this.currentProject ? Number(this.currentProject) : undefined;

    // Der Platzhalter `pending-…` wird bewusst NICHT mitgeschickt: solange kein echtes Regelwerk
    // existiert, muss das Feld leer bleiben, damit der Agent die Anlege-Werkzeuge bekommt und den
    // Typ selbst wählen darf. Die Zuordnung nach einem Reload läuft über die echte ID.
    const rulesetId = surface !== 'classic' && this.activeMycheckRulesetId
      ? this.activeMycheckRulesetId
      : undefined;

    const thread = await this.chatApiService.createThread(projectId, {
      mode,
      rulesetId,
      mycheckType: surface !== 'classic' ? this.activeMycheckType : undefined,
    });

    const newThread = {id: thread.id, topic: thread.topic, createdOn: new Date(thread.createdAt)};
    if (mode === 'CLASSIC') {
      this.threads.unshift(newThread);
    }
    this.updateCurrentChat(surface, newThread);

    if (shouldNavigate) {
      this.router.navigate(['projects', this.currentProject, 'chat', newThread.id]);
    }

    return thread.id;
  }

  canLoadMoreChats(): boolean {
    return !this.allThreadsLoaded;
  }

  /**
   * Das Backend hat den Turn abgelehnt, weil das Kontingent des Projekts ausgeschöpft ist.
   *
   * <p>Wie bei `attachToRunningTurn`: die Frage wurde <strong>nicht</strong> gesendet. Sie wandert
   * zurück ins Eingabefeld, die optimistische Nutzernachricht und die leere Antwortblase
   * verschwinden. Ab jetzt ist der Senden-Button mit Begründung gesperrt.
   */
  private rejectForQuota(surface: ChatSurface, streaming: Message, unsentQuestion: string,
                         quota: ChatQuotaDto | undefined): void {
    const session = this.sessions[surface];
    // Ohne Rumpf trotzdem sperren: der Server hat 402 gesagt.
    this.applyChatQuota({...(quota ?? {used: 0}), exceeded: true} as ChatQuotaDto);

    // In place wie in attachToRunningTurn: die Ansicht rendert dieselbe Liste.
    for (let i = session.messages.length - 1; i >= 0; i--) {
      const m = session.messages[i];
      if (m === streaming || (m.sender === 'user' && m.message === unsentQuestion && m.id.startsWith('local-'))) {
        session.messages.splice(i, 1);
      }
    }
    if (!session.question?.trim()) {
      session.question = unsentQuestion;
    }
    session.streamingMessage = undefined;
    this.setBotThinking(session, false);
    session.toolSteps = [];
    this.turnFinished.emit(surface);
  }

  async deleteChat(chatThreadId: string) {
    await this.chatApiService.deleteThread(chatThreadId);
    // Nur die Oberfläche leeren, die diesen Thread tatsächlich offen hatte.
    const session = this.sessionForThread(chatThreadId);
    if (session) {
      this.resetChatState(session.surface);
    }
    this.threads = this.threads.filter(thread => thread.id !== chatThreadId);
  }

  /**
   * Verarbeitet ein Stream-Ereignis.
   *
   * <p>Die Nachricht wird **in place** fortgeschrieben – die Ansicht rendert dieselbe Instanz, die
   * hier wächst. Ein Ersetzen des Objekts würde bei jedem Token die Zeile neu aufbauen.
   */
  private applyStreamEvent(surface: ChatSurface, streaming: Message, event: ChatStreamEvent) {
    const session = this.sessions[surface];

    switch (event.type) {
      case 'TURN_STARTED':
        session.currentTurnId = event.data['turnId'] ?? session.currentTurnId;
        break;

      case 'DELTA':
        streaming.message = (streaming.message ?? '') + (event.data['text'] ?? '');
        session.messagesScrollDown.emit();
        break;

      case 'STEP': {
        const step: ChatToolStep = {
          stepId: event.data['stepId'],
          label: event.data['label'],
          status: event.data['status'],
          detail: event.data['detail'],
        };
        const existing = session.toolSteps.findIndex(s => s.stepId === step.stepId);
        if (existing >= 0) {
          session.toolSteps[existing] = step;
        } else {
          session.toolSteps = [...session.toolSteps, step];
        }
        break;
      }

      case 'UI_BLOCK':
        streaming.uiBlock = this.normalizeUiBlocks(event.data['blocks']);
        session.messagesScrollDown.emit();
        break;

      case 'SOURCES':
        // Die Belege der Antwort ("Referenzen"). Sie kommen aus den Suchwerkzeugen, nicht aus dem
        // Antworttext – das Modell soll Dateien benennen, nicht Kennungen zitieren.
        streaming.files = (event.data['sources'] ?? []).map((source: any) => ({
          id: source.dataId,
          dataId: source.dataId,
          fileName: source.fileName,
          page: source.page,
        }));
        // Referenzkarten wachsen unter der Antwort - sonst verschwaenden sie unter dem Rand.
        session.messagesScrollDown.emit();
        break;

      case 'SIGNAL':
        this.handleStreamSignal(event.data);
        break;

      case 'VIEWER_ACTION':
        this.handleViewerAction(event.data as ChatViewerAction);
        break;

      case 'VIEWER_QUERY':
        this.handleViewerQuery(event.data as ChatViewerQuery);
        break;

      case 'ABORTED':
        session.currentTurnId = undefined;
        streaming.id = event.data['messageId'] ?? streaming.id;
        if (event.data['reason'] === 'cancelled') {
          // Vom Nutzer gestoppt: kein Fehler, der Teiltext bleibt stehen.
          streaming.message = (event.data['text'] || streaming.message || '') + '\n\n_Gestoppt._';
        } else {
          streaming.message = (streaming.message ?? '')
            + '\n\n_Die Antwort wurde abgebrochen. Du kannst die Frage erneut senden._';
        }
        this.setBotThinking(session, false);
        session.toolSteps = [];
        break;

      case 'DONE':
        session.currentTurnId = undefined;
        // Der vollständige Text kommt bewusst noch einmal mit: gingen einzelne Deltas verloren,
        // korrigiert sich die Anzeige hier selbst, ohne den Verlauf neu laden zu müssen.
        streaming.id = event.data['messageId'] ?? streaming.id;
        // `||` statt `??`: ein leerer String ist kein Ergebnis und darf den gestreamten Text nicht
        // überschreiben – sonst stand nach einem Abbruch eine leere Sprechblase da.
        streaming.message = event.data['text'] || streaming.message;
        if (event.data['abortReason'] === 'max_rounds') {
          streaming.message += '\n\n_Die Antwort ist möglicherweise unvollständig – grenze die Frage ein, '
            + 'dann kann ich gezielter nachsehen._';
        }
        this.setBotThinking(session, false);
        session.toolSteps = [];
        // Der vollstaendige Text ersetzt den gestreamten, und die Schritt-Anzeige verschwindet -
        // beides aendert die Hoehe, also ein letztes Mal nachziehen.
        session.messagesScrollDown.emit();
        break;

      case 'ERROR':
        session.currentTurnId = undefined;
        streaming.message = event.data['message'] ?? 'Es ist ein Fehler aufgetreten.';
        this.setBotThinking(session, false);
        session.toolSteps = [];
        break;

      default:
        break;
    }
  }

  private clearSelectedContext(surface: ChatSurface): void {
    const session = this.sessions[surface];
    session.selectedRuns = [];
    session.BIM_Check = undefined;
    session.LOI_Check = undefined;
    session.QUALITY_Check = undefined;
    session.HOAI_Check = undefined;
    session.selectedFiles = [];
  }

  /**
   * Sichtbarer Hinweis, wenn der Bot nicht geantwortet hat.
   *
   * <p>Ohne ihn gäbe der Watchdog die Eingabe stumm wieder frei – der Nutzer wüsste nicht, ob
   * seine Frage angekommen ist und ob eine Antwort noch nachkommt.
   */
  private addBotTimeoutEventMessage(session: ChatSession): void {
    if (!session.currentChat?.id) return;
    this.addMessageToChat(session.surface, {
      id: `bot-timeout-${Date.now()}`,
      message: 'Der Assistent hat nicht geantwortet. Sie können Ihre Frage erneut senden.',
      createdOn: new Date(),
      metadata: {
        eventType: 'bot_timeout',
        mode: session.surface,
      },
      sender: 'system',
      uiBlock: undefined,
      files: undefined,
      table: undefined,
    }, false);
  }

  addFileContextEventMessage(surface: ChatSurface, fileNames: string[]): void {
    if (!this.sessions[surface].currentChat?.id || fileNames.length === 0) return;

    const uniqueFileNames = fileNames
      .map(fileName => fileName.trim())
      .filter(Boolean)
      .filter((fileName, index, names) => names.indexOf(fileName) === index);
    if (uniqueFileNames.length === 0) return;

    const previewFileNames = uniqueFileNames.slice(0, 3).join(', ');
    const additionalCount = uniqueFileNames.length - 3;
    const suffix = additionalCount > 0 ? ` +${additionalCount} weitere` : '';
    const messageText = uniqueFileNames.length === 1
      ? `1 Datei als Kontext hinzugefügt: ${previewFileNames}${suffix}`
      : `${uniqueFileNames.length} Dateien als Kontext hinzugefügt: ${previewFileNames}${suffix}`;

    this.addMessageToChat(surface, {
      id: `context-files-${Date.now()}`,
      message: messageText,
      createdOn: new Date(),
      metadata: {
        eventType: 'context_files',
        mode: surface,
        fileNames: JSON.stringify(uniqueFileNames)
      },
      sender: 'system',
      uiBlock: undefined,
      files: undefined,
      table: undefined,
    }, false);
  }

  registerUiBlockSelection(
    surface: ChatSurface,
    optionValues: string[],
    blockIndex: number,
    uiBlockType: UiBlock['type'],
    messageId?: string
  ): void {
    const normalizedValues = optionValues
      .map(value => value.trim())
      .filter(value => value.length > 0);
    if (normalizedValues.length === 0) {
      this.clearPendingUiBlockSelection(surface);
      return;
    }

    this.sessions[surface].pendingUiBlockSelection = {
      messageId,
      blockIndex,
      uiBlockType,
      optionValues: normalizedValues,
    };
  }

  clearPendingUiBlockSelection(surface: ChatSurface): void {
    this.sessions[surface].pendingUiBlockSelection = undefined;
  }

  /**
   * Der Thread, dessen letzte Nachrichten der Feature-Anfrage beiliegen – nur ein vom Server bekannter:
   * lokale und Stream-Ids kennt das Backend nicht. Ohne Thread geht kein Chatverlauf mit.
   */
  featureRequestThreadId(surface: ChatSurface): string | undefined {
    const threadId = this.sessions[surface].currentChat?.id;
    return threadId && !threadId.startsWith('streaming-') && !threadId.startsWith('local-') ? threadId : undefined;
  }

  /** Bereits gesendete Feature-Anfragen; überlebt den Neuaufbau der Nachricht (und damit des Panels). */
  private readonly sentFeatureRequests = new Set<string>();

  /**
   * Lässt chatBIC aus dem Chat der Fläche einen Vorschlag für die Feature-Anfrage erzeugen – für Blöcke,
   * an denen das Modell keinen `featureProposal` geliefert hat. Ohne Projekt oder ohne serverseitigen
   * Thread gibt es nichts zu lesen: dann ein Fehler ohne Backend-Aufruf. Fehlende Felder werden zu `''`.
   */
  requestFeatureProposal(surface: ChatSurface, uiBlock?: UiBlock): Observable<UiBlockFeatureProposal> {
    const projectId = this.stateConfigService.getProjectId();
    const threadId = this.featureRequestThreadId(surface);
    if (!projectId || !threadId) {
      return throwError(() => new Error('Feature-Vorschlag braucht ein Projekt und einen serverseitigen Thread.'));
    }
    return this.featureProposalApi.propose(projectId, {
      threadId,
      unavailableText: uiBlock?.unavailableText?.trim() || undefined,
    }).pipe(
      map(proposal => ({
        title: proposal?.title ?? '',
        problem: proposal?.problem ?? '',
        desiredBehavior: proposal?.desiredBehavior ?? '',
        area: proposal?.area ?? '',
      })),
      timeout({first: FEATURE_PROPOSAL_TIMEOUT_MS}),
    );
  }

  /**
   * Sendet die vom Nutzer bestätigte (ggf. bearbeitete) Feature-Anfrage mit den strukturierten Feldern.
   * Den Chatverlauf liest das Backend selbst anhand der `threadId`; er wird hier nicht mitgeschickt.
   * Mit `includeChatHistory = false` lässt das Backend ihn aus der Mail weg.
   */
  submitUnavailableFeatureRequest(surface: ChatSurface, uiBlock: UiBlock | undefined, proposal: UiBlockFeatureProposal,
                                  includeChatHistory: boolean, blockRef?: FeatureRequestBlockRef): Observable<{
    [key: string]: string;
  }> {
    const projectId = this.stateConfigService.getProjectId();
    if (!projectId) {
      throw new Error('Project id is required to submit a feature request.');
    }

    const user = this.stateConfigService.user;
    const userName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
    const request: MyCheckFeatureRequestRequest = {
      title: proposal.title.trim(),
      problem: proposal.problem.trim(),
      desiredBehavior: proposal.desiredBehavior.trim(),
      area: proposal.area.trim(),
      includeChatHistory,
      threadId: this.featureRequestThreadId(surface),
      workflowId: this.activeMycheckRulesetId,
      userId: user?.id != null ? user.id.toString() : undefined,
      userName: userName || undefined,
      userEmail: user?.mail || undefined,
    };

    return this.workflowDefinitionControllerService.submitMyCheckFeatureRequest(request, projectId).pipe(
      tap(() => this.sentFeatureRequests.add(this.featureRequestKey(surface, uiBlock, blockRef))),
    );
  }

  hasSentFeatureRequest(surface: ChatSurface, uiBlock?: UiBlock, blockRef?: FeatureRequestBlockRef): boolean {
    return this.sentFeatureRequests.has(this.featureRequestKey(surface, uiBlock, blockRef));
  }

  /** Je Block eindeutig: Nachricht und Position gehören zum Schlüssel, damit gleiche Blöcke sich nicht sperren. */
  private featureRequestKey(surface: ChatSurface, uiBlock?: UiBlock, blockRef?: FeatureRequestBlockRef): string {
    return `${surface}|${this.featureRequestThreadId(surface) ?? ''}|${blockRef?.messageId ?? ''}|${blockRef?.blockIndex ?? ''}|${JSON.stringify(uiBlock ?? null)}`;
  }

  getActiveOptionUiBlocks(surface: ChatSurface): { messageId: string; blockIndex: number; uiBlock: UiBlock }[] {
    // Bei gleichem Zeitstempel zählt die Reihenfolge der Liste (Array.sort ist stabil), NICHT die
    // ID: Frage und Antwort eines Turns entstehen in derselben Millisekunde, und nach DONE trägt die
    // Antwort die Server-UUID (Hex) – die sortierte per localeCompare immer vor `local-…`, die Frage
    // landete dahinter und verwarf die Auswahloptionen der Antwort.
    const orderedMessages = [...this.sessions[surface].messages]
      .sort((left, right) => left.createdOn.getTime() - right.createdOn.getTime());

    let activeOptionBlocks: { messageId: string; blockIndex: number; uiBlock: UiBlock }[] = [];
    for (const message of orderedMessages) {
      if (message.sender === 'user') {
        activeOptionBlocks = [];
        continue;
      }
      if (!message.uiBlock || message.uiBlock.length === 0) {
        continue;
      }

      const optionBlocks = message.uiBlock
        .map((uiBlock, blockIndex) => ({messageId: message.id, blockIndex, uiBlock}))
        .filter(entry => entry.uiBlock.type === 'options');
      if (optionBlocks.length > 0) {
        activeOptionBlocks = optionBlocks;
      }
    }

    return activeOptionBlocks;
  }

  private parseChannelData(rawChannelData: string | undefined): Record<string, unknown> | undefined {
    if (!rawChannelData) return undefined;
    try {
      const parsed = JSON.parse(rawChannelData);
      return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : undefined;
    } catch {
      return undefined;
    }
  }

  private isTruthyFlag(value: unknown): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.toLowerCase() === 'true';
    return false;
  }

  /** Fachliche Signale des Agenten – etwa ein angelegtes Regelwerk. */
  /**
   * Führt eine Anweisung des Agenten im offenen IFC-Viewer aus und meldet das Ergebnis zurück.
   *
   * <p>Ohne offenen Viewer geht ein Fehler zurück: der Agent wartet auf die Antwort und soll dem
   * Nutzer sagen können, warum nichts passiert ist.
   */
  private handleViewerAction(action: ChatViewerAction) {
    if (!this.claimViewerCall(action)) return;
    void this.answerViewerCall(action, () => this.injector.get(BimViewerService).applyChatAction(action));
  }

  /** Beantwortet eine Abfrage des Agenten über das offene Modell (Properties, Werte, Anzahl). */
  private handleViewerQuery(query: ChatViewerQuery) {
    if (!this.claimViewerCall(query)) return;
    void this.answerViewerCall(query, () => this.injector.get(BimViewerService).queryForChat(query));
  }

  /** Jeder Aufruf wird nur einmal ausgeführt – auch wenn ihn eine Wiederaufnahme erneut abspielt. */
  private claimViewerCall(call: ChatViewerAction | ChatViewerQuery): boolean {
    const id = call?.callId ?? call?.actionId;
    if (!id || this.viewerActionsHandled.has(id)) return false;
    this.viewerActionsHandled.add(id);
    return true;
  }

  /**
   * Führt aus und schickt das Ergebnis – oder den Fehler – an den wartenden Turn.
   *
   * <p>Ein 409 heißt, ein anderer Tab war schneller oder der Turn hat aufgehört zu warten; das ist
   * kein Fehler und wird in {@link ChatStreamService.answerClientCall} geschluckt.
   */
  private async answerViewerCall(call: ChatViewerAction | ChatViewerQuery, run: () => Promise<unknown>) {
    let body: { ok: boolean; result?: unknown; error?: string };
    try {
      if (!this.activeViewerContext) throw new Error('Der Nutzer hat den IFC-Viewer nicht mehr offen.');
      body = {ok: true, result: await run()};
    } catch (err: any) {
      console.error('chatBIC: Viewer-Aufruf fehlgeschlagen', call, err);
      body = {ok: false, error: err?.message ?? String(err)};
    }
    // Ereignisse eines Servers ohne Rückkanal (oder aus alten Protokollen) tragen keine callId.
    if (!call.callId || !call.turnId) return;
    try {
      await this.chatStreamService.answerClientCall(call.turnId, call.callId, body);
    } catch (err) {
      console.error('chatBIC: Viewer-Ergebnis konnte nicht gemeldet werden', call, err);
    }
  }

  /** Der Viewer-Kontext für den Turn – nur, solange der Nutzer im Viewer steht. */
  private async viewerSnapshotForTurn() {
    if (!this.activeViewerContext) return undefined;
    try {
      return await this.injector.get(BimViewerService).snapshotForChat(this.activeViewerContext.fileName);
    } catch (err) {
      // Die Frage soll trotzdem rausgehen – dann eben ohne Modellbezug. Laut, nicht still.
      console.error('chatBIC: Viewer-Kontext konnte nicht gelesen werden', err);
      return undefined;
    }
  }

  private handleStreamSignal(data: Record<string, any>) {
    if (data['filesChanged']) {
      this.pageContext.notifyDataChanged('files');
    }
    const rulesetId = data['rulesetId'];
    const mycheckType = data['mycheckType'];
    const series = data['mycheckSeries'] as ChatMycheckSeriesDto | undefined;
    if (series) {
      this.mycheckSeriesProgressed = this.seriesProgressed(this.mycheckSeries, series);
      this.applyMycheckSeries(series);
    }
    // Vor der Übernahme festhalten: sie setzt den Platzhalter zurück.
    const pendingKey = this.pendingMycheckKey;
    const adopted = data['rulesetCreated'] && rulesetId
      ? this.adoptCreatedMycheck(rulesetId, this.normalizeMycheckType(mycheckType))
      : Promise.resolve();
    // In einer laufenden Serie ist ein startbereiter myCheck nur ein Zwischenstand – der Quickstart
    // bleibt offen, bis alle Punkte erledigt sind.
    const seriesRunning = !!this.mycheckSeries && !this.mycheckSeries.finished;
    if (data['rulesetCompleted'] && rulesetId && !seriesRunning) {
      // Aufräumen und Abschluss-Event erst nach der Übernahme: sie verschiebt den Merker vom
      // Platzhalter auf die echte ID (würde ein vorheriges Aufräumen rückgängig machen) und meldet
      // `mycheckRulesetCreated` – „angelegt" muss vor „fertig" bei den Abonnenten ankommen.
      void adopted.finally(() => this.finishMycheckQuickstart(rulesetId, rulesetId, pendingKey));
    }
    // Vorschlagssignal des Edit-Modus – nur beim Übergang, nicht bei jeder Folgeantwort, die
    // denselben weiterhin offenen Vorschlag nennt. Der Server sendet `proposalCreated` zwar bereits
    // nur beim Wechsel; die Entprellung bleibt trotzdem, weil dasselbe Ereignis nach einem
    // Verbindungsabbruch über die Wiederaufnahme ein zweites Mal ankommen kann.
    // Fallback auf den offenen myCheck: die Propose-Werkzeuge melden keine `rulesetId`, der Edit-Chat
    // gehört aber immer zu genau einem myCheck.
    const proposalId = data['proposalId'];
    const proposalRulesetId = rulesetId ?? this.activeMycheckRulesetId;
    if (data['proposalCreated'] && proposalId && proposalRulesetId && !this.proposalSignalsHandled.has(proposalId)) {
      this.proposalSignalsHandled.add(proposalId);
      this.mycheckProposalReceived.emit({rulesetId: proposalRulesetId, proposalId});
    }
  }

  /** Übernimmt den Stand einer Serie (Signal oder geladener Verlauf). */
  private applyMycheckSeries(series: ChatMycheckSeriesDto | undefined): void {
    this.mycheckSeries = series;
    this.mycheckSeriesChanged.emit(series);
  }

  /**
   * Vorangekommen heißt: ein Punkt weniger offen, der Plan gerade bestätigt oder der Prüflauf
   * angelegt. Ein Turn ohne Fortschritt (Rückfrage, Fehler) darf kein weiteres Weiter auslösen.
   */
  private seriesProgressed(before: ChatMycheckSeriesDto | undefined, after: ChatMycheckSeriesDto): boolean {
    const open = (s: ChatMycheckSeriesDto | undefined) =>
      (s?.items ?? []).filter(item => item.status === 'PLANNED').length;
    if (!after.confirmed) return false;
    if (!before?.confirmed) return true;
    return open(after) < open(before) || (!before.checkRunDefinitionId && !!after.checkRunDefinitionId);
  }

  /**
   * Startet den nächsten Schritt einer Serie, sobald der vorige Turn fertig ist: den nächsten
   * myCheck oder am Ende den Prüflauf. Nur nach echtem Fortschritt und nicht, solange angehalten.
   */
  private maybeContinueMycheckSeries(): void {
    const series = this.mycheckSeries;
    const progressed = this.mycheckSeriesProgressed;
    this.mycheckSeriesProgressed = false;
    if (!series?.confirmed || !progressed || this.mycheckSeriesPaused) return;
    if (!series.next && !series.checkRunDue) return;
    window.clearTimeout(this.mycheckSeriesTimer);
    this.mycheckSeriesTimer = window.setTimeout(() => void this.sendMycheckSeriesNext(),
      ChatService.MYCHECK_SERIES_NEXT_DELAY_MS);
  }

  /** Der nächste Schritt der Serie als gewöhnliche Nachricht – auch der Knopf „Weiter" geht hierüber. */
  async sendMycheckSeriesNext(): Promise<void> {
    const series = this.mycheckSeries;
    const threadId = this.sessions.mycheck.currentChat?.id;
    if (!series || !threadId || this.sessions.mycheck.isBotThinking) return;
    const text = series.next
      ? `Weiter mit „${series.next.name}"`
      : series.checkRunDue ? 'Prüflauf aus den fertigen myChecks anlegen' : undefined;
    if (!text) return;
    this.registerUiBlockSelection('mycheck', [ChatService.MYCHECK_SERIES_NEXT], 0, 'options');
    await this.sendMessage('mycheck', threadId, text);
  }

  pauseMycheckSeries(): void {
    this.mycheckSeriesPaused = true;
    window.clearTimeout(this.mycheckSeriesTimer);
  }

  resumeMycheckSeries(): void {
    this.mycheckSeriesPaused = false;
    void this.sendMycheckSeriesNext();
  }

  /** Liest ein gespeichertes JSON-Feld; unlesbare Inhalte dürfen den Verlauf nicht sprengen. */
  private parseJsonSafe(value: string | undefined): any {
    if (!value) return undefined;
    try {
      return JSON.parse(value);
    } catch {
      return undefined;
    }
  }

  private updateContextFromUrl(url: string): void {
    // Query und Fragment vor dem Zerlegen abtrennen: sonst hing `?instanceId=…` am letzten Segment,
    // und `check-runs/execute?instanceId=X` wurde als Prüflauf mit der Kennung „execute?…" gelesen.
    const [pathPart, queryPart] = url.split('#')[0].split('?');
    const query = new URLSearchParams(queryPart ?? '');
    const segments = pathPart.split('/').filter(s => !!s);
    let module = 'unknown';
    let projectId: number | undefined;
    let checkId: string | undefined;
    let checkType: PlatformContext['checkType'];

    // Pattern: /projects/:id/<module>/...
    const projectsIdx = segments.indexOf('projects');
    if (projectsIdx !== -1 && segments.length > projectsIdx + 1) {
      projectId = Number(segments[projectsIdx + 1]);
      if (segments.length > projectsIdx + 2) {
        module = segments[projectsIdx + 2]; // e.g. 'dashboard', 'files', 'tasks', 'quality', 'chat', 'reports', etc.
      }

      // Detect check result routes and extract check IDs
      // Quality: /projects/:id/quality/:checkId/report
      const qualityIdx = segments.indexOf('quality');
      if (qualityIdx !== -1 && segments.length > qualityIdx + 1) {
        const candidate = segments[qualityIdx + 1];
        if (candidate !== 'rules' && candidate !== 'choose-files') {
          checkId = candidate;
          checkType = 'QUALITY';
        }
      }

      // All-in-check sub-routes
      const allInIdx = segments.indexOf('all-in-check');
      if (allInIdx !== -1) {
        // HOAI: /projects/:id/all-in-check/hoai/:checkId/details
        const hoaiIdx = segments.indexOf('hoai', allInIdx);
        if (hoaiIdx !== -1 && segments.length > hoaiIdx + 1) {
          const candidate = segments[hoaiIdx + 1];
          if (!['choose-rules', 'choose-files', 'custom', 'loading'].includes(candidate)) {
            checkId = candidate;
            checkType = 'HOAI';
          }
        }

        // BIM/LOI: /projects/:id/all-in-check/bim-check/:reportId/(base-check|loi-check|ids-check)
        const bimCheckIdx = segments.indexOf('bim-check', allInIdx);
        if (bimCheckIdx !== -1 && segments.length > bimCheckIdx + 1) {
          const candidate = segments[bimCheckIdx + 1];
          if (!['choose-rules', 'choose-files', 'custom', 'loading'].includes(candidate)) {
            checkId = candidate;
            const subRoute = segments[bimCheckIdx + 2];
            if (subRoute === 'loi-check' || subRoute === 'ids-check') {
              checkType = 'LOI';
            } else {
              checkType = 'BIM';
            }
          }
        }
      }
    }

    // Das konkrete Objekt hinter dem Modul. Bewusst nur dort, wo auf das Modulsegment eine
    // Kennung folgt und kein Unterpfad wie 'new' oder 'overview'.
    let entityKind: ChatContextEntityKind | undefined;
    let entityId: string | undefined;
    let entityLabelFromRoute: string | undefined;
    if (projectsIdx !== -1) {
      const entityCandidate = segments[projectsIdx + 3];
      const kindByModule: Record<string, ChatContextEntityKind> = {
        'my-checks': 'mycheck',
        'check-runs': 'checkrun',
        'tasks': 'task',
        'reports': 'report',
      };
      const kind = kindByModule[module];
      if (module === 'check-runs' && (entityCandidate === 'execute' || entityCandidate === 'log')) {
        // Fortschritt bzw. Protokoll eines Laufs: die Kennung steht in der Query, nicht im Pfad.
        const instanceId = query.get('instanceId');
        if (instanceId) {
          entityKind = 'run';
          entityId = instanceId;
        }
      } else if (module === 'check-runs' && entityCandidate === 'builder' && segments[projectsIdx + 4]) {
        entityKind = 'checkrun';
        entityId = segments[projectsIdx + 4];
      } else if (kind && entityCandidate && !ChatService.NON_ENTITY_SEGMENTS.includes(entityCandidate)) {
        entityKind = kind;
        entityId = entityCandidate;
        // Die Berichtsliste gibt den myCheck-Namen als `checkName` mit – ein Abruf wäre unnötig.
        if (kind === 'report') entityLabelFromRoute = query.get('checkName') ?? undefined;
      }
    }

    // Top-level routes
    if (module === 'unknown') {
      if (segments.includes('organisations')) module = 'organisations';
      else if (segments.includes('tasks')) module = 'tasks';
      else if (segments.includes('reports')) module = 'reports';
      else if (segments.includes('profile')) module = 'profile';
    }

    // Der 3D-Viewer laeuft unter /projects/:id/files/:fileId, das Segment sagt also 'files'. Ist ein
    // Modell registriert, gewinnt der Viewer-Modul-Wert. Die Beschraenkung auf die beiden
    // Datei-Segmente sorgt dafuer, dass eine liegengebliebene Registrierung keine fremde Seite
    // falsch labelt – und raeumt sie gleich mit auf.
    let viewer = this.activeViewerContext;
    if (viewer && (module === 'files' || module === 'ifc-viewer')) {
      module = ChatService.VIEWER_MODULE;
    } else if (viewer) {
      this.activeViewerContext = undefined;
      viewer = undefined;
    }

    const prevContext = this.currentPlatformContext;
    this.currentPlatformContext = {
      route: url,
      module,
      projectId,
      checkId,
      checkType,
      entityKind,
      entityId,
      viewerFileId: viewer?.fileId,
      viewerFileName: viewer?.fileName,
      viewerModelCount: viewer?.models.length,
    };

    // Den Namen des Objekts nachladen, sobald sich das Objekt aendert. Bewusst nach dem Setzen des
    // Kontexts: die Aufloesung ist asynchron, der Rest der Anwendung darf nicht darauf warten.
    if (prevContext.entityId !== entityId || prevContext.entityKind !== entityKind) {
      this.contextEntityLabel = entityLabelFromRoute;
      if (entityKind && entityId && !entityLabelFromRoute) {
        const resolvedFor = entityId;
        void this.chatContextLabelService.resolve(entityKind, entityId, projectId).then(name => {
          // Zwischenzeitlich weitergeklickt? Dann gehoert der Name nicht mehr hierher.
          if (this.currentPlatformContext.entityId === resolvedFor) {
            this.contextEntityLabel = name;
          }
        });
      }
    }

    // Auto-set check context when navigating to a check result page
    if (checkId && checkType && (prevContext.checkId !== checkId || prevContext.checkType !== checkType)) {
      this.autoSetCheckFromContext(checkId, checkType, projectId!);
    }
  }

  private autoSetCheckFromContext(checkId: string, checkType: NonNullable<PlatformContext['checkType']>, projectId: number): void {
    switch (checkType) {
      case 'QUALITY':
        this.qualityControllerService.getChecks(projectId).subscribe(checks => {
          const check = checks.find(c => c.id === checkId);
          if (check) this.QUALITY_Check = check;
        });
        break;
      case 'HOAI':
        this.hoaiCheckControllerService.getHOAIChecks(projectId).subscribe(checks => {
          const check = checks.find(c => c.id === checkId);
          if (check) this.HOAI_Check = check;
        });
        break;
      case 'BIM':
        // checkId from URL is the IFC check ID (reportId).
        this.bimBaseController.getChecks2(projectId).subscribe(checks => {
          const check = checks.find(c => c.checkId === checkId || c.id === checkId);
          if (check) {
            this.BIM_Check = check;
          } else {
            // Fallback: load the IFC check to resolve the BIM base check ID
            this.ifcCheckController.getCheck1(checkId, projectId).subscribe(ifcCheck => {
              if (ifcCheck?.bimBaseCheck?.id) {
                const bimCheck = checks.find(c => c.id === ifcCheck.bimBaseCheck!.id);
                if (bimCheck) this.BIM_Check = bimCheck;
              }
            });
          }
        });
        break;
      case 'LOI':
        this.ifcCheckController.getChecks1(projectId).subscribe(checks => {
          const check = checks.find(c => c.id === checkId);
          if (check) this.LOI_Check = check;
        });
        break;
    }
  }

  /** Stellt den über die Route adressierten Verlauf wieder her. */
  private async restoreThreadFromRouting(): Promise<void> {
    const threadId = this.chatIdFromRouting;
    if (!threadId) return;
    try {
      const threads = await this.chatApiService.listThreads(
        this.currentProject ? Number(this.currentProject) : undefined, 'CLASSIC', 0, this.threadsPerPage);
      const match = threads.find(t => t.id === threadId);
      const restored: ChatThreadEntry = {
        id: threadId,
        topic: match?.topic ?? 'Chat',
        createdOn: new Date(match?.createdAt ?? Date.now()),
      };
      this.setCurrentChat('classic', restored);
      await this.getChatMessages('classic', restored.id);
    } catch (error) {
      console.error('Chat aus der Route konnte nicht geladen werden:', error);
    }
  }

  private initializeUserProjects() {
    this.projects = [];
    this.stateConfigService.projects.forEach((value, key) => {
      this.projects.push({id: String(key), name: String(value.name)});
    });
  }

  /**
   * Merkt, dass für dieses myCheck der Quickstart läuft (überlebt einen Reload).
   *
   * <p>Der Name wird mitgespeichert, weil die Topbar ihn anzeigt („myCheck - <Name>") und er nach
   * einem Reload sonst erst über die neu geladene Liste verfügbar wäre.
   */
  rememberMycheckQuickstartActive(rulesetId: string, name?: string): void {
    const projectId = this.stateConfigService.getProjectId();
    if (!projectId || !rulesetId) return;
    const active = this.readMycheckQuickstartActive();
    const key = this.getMycheckBootstrapKey(projectId, rulesetId);
    active[key] = (name ?? active[key] ?? '').trim();
    this.writeStorageMap(this.mycheckQuickstartActiveStorageKey, active);
  }

  private async changeTopic(chatThreadId: string, newTopic: string) {
    await this.chatApiService.rename(chatThreadId, newTopic);
    // Der Thread kann in jeder Oberfläche offen sein.
    const session = this.sessionForThread(chatThreadId);
    if (session?.currentChat) {
      session.currentChat.topic = newTopic;
    }
  }

  private addMessage(session: ChatSession, message: Message, unshift: boolean = true) {
    if (unshift) {
      session.messages.unshift(message);
    } else {
      session.messages.push(message);
    }
    session.messages = session.messages.filter((currentElement, index, array) =>
      index === array.findIndex(findTest => findTest.id === currentElement.id)
    );
    session.messagesScrollDown.emit();
  }

  isMycheckQuickstartActive(rulesetId: string): boolean {
    const projectId = this.stateConfigService.getProjectId();
    if (!projectId || !rulesetId) return false;
    const key = this.getMycheckBootstrapKey(projectId, rulesetId);
    return Object.prototype.hasOwnProperty.call(this.readMycheckQuickstartActive(), key);
  }

  private isTruthyMetadataFlag(value: string | undefined): boolean {
    return value?.toLowerCase() === 'true';
  }

  private createMessage(partial: {
    id: string;
    message?: string;
    createdOn: Date;
    metadata?: Record<string, string>;
    sender?: string;
    uiBlock?: UiBlock[];
  }): Message {
    return {
      id: partial.id,
      message: partial.message,
      createdOn: partial.createdOn,
      metadata: partial.metadata,
      sender: partial.sender,
      uiBlock: partial.uiBlock,
      files: undefined,
      table: undefined,
    };
  }

  /**
   * Stellt sicher, dass der Chat auf dem Thread dieses myChecks steht – und legt nur dann einen
   * neuen an, wenn nachweislich keiner existiert.
   *
   * <p>Ein Fehler bei der Suche (z.B. 429 von ACS) wird bewusst **durchgereicht** statt als "kein
   * Thread gefunden" behandelt: sonst entstünde bei jedem gedrosselten Aufruf ein weiterer Thread.
   */
  async ensureMycheckThread(mycheckKey: string, isPending = false, surface: 'mycheck' | 'edit' = 'mycheck'): Promise<void> {
    // `isPending` = der myCheck existiert noch nicht (typoffener Start). Dann darf der Schlüssel
    // NICHT in activeMycheckRulesetId landen, weil er von dort in die Bot-Metadata wanderte.
    if (isPending) {
      this.pendingMycheckKey = mycheckKey;
      this.activeMycheckRulesetId = undefined;
    } else {
      this.activeMycheckRulesetId = mycheckKey;
      this.pendingMycheckKey = undefined;
    }
    const rulesetId = mycheckKey;
    // Die Oberfläche kommt als Parameter, nicht aus `activeSurface`: der Quickstart läuft damit
    // unverändert auf 'mycheck', nur der Edit-Modus übergibt ausdrücklich 'edit'.
    const session = this.sessions[surface];
    const matchingThread = await this.findMycheckThread(rulesetId);
    if (matchingThread) {
      this.rememberMycheckThreadId(rulesetId, matchingThread.id);
      const currentChatId = session.currentChat?.id;
      this.updateCurrentChat(surface, matchingThread);
      if (currentChatId !== matchingThread.id || session.messages.length === 0) {
        await this.getChatMessages(surface, matchingThread.id);
      }
      return;
    }

    const newThreadId = await this.createChat(surface);
    if (newThreadId) {
      this.rememberMycheckThreadId(rulesetId, newThreadId);
      await this.getChatMessages(surface, newThreadId);
    }
  }

  /**
   * Öffnet die Chat-Sidebar im Edit-Modus für ein **bestehendes** myCheck (Button „Mit chatBIC
   * bearbeiten" im Editor).
   *
   * <p>Nutzt bewusst nicht {@link rememberMycheckQuickstartActive}/den Quickstart-Aktiv-Speicher im
   * sessionStorage: ein Editor, der beim Laden {@link isMycheckQuickstartActive} prüft, würde sein
   * Formular sonst fälschlich sperren, obwohl es im Edit-Modus laut Vorgabe bedienbar bleiben soll.
   * Der Thread-ID-Cache ({@link rememberMycheckThreadId}) wird dagegen ganz normal über
   * {@link ensureMycheckThread} mitbenutzt – das ist kein Quickstart-Merker, sondern der
   * "ein Thread pro myCheck"-Mechanismus, über den auch F5 den Thread wiederfindet.
   */
  async openMycheckEditChat(rulesetId: string, mycheckType: 'KI' | 'IDS' | 'HYBRID'): Promise<void> {
    if (!rulesetId) return;
    const projectId = this.stateConfigService.getProjectId();
    if (!projectId) return;

    const chatReady = await this.ensureChatReadyForProject(projectId);
    if (!chatReady) return;

    this.activeSurface = 'edit';
    this.activeMycheckType = mycheckType;
    this.isFloatingMode = true;
    await this.ensureMycheckThread(rulesetId, false, 'edit');
    this.floatingChatOpen = true;
  }

  private addMycheckBootstrapPlaceholderMessage(checkName: string): void {
    this.addMessageToChat('mycheck', {
      id: `mycheck-bootstrap-${Date.now()}`,
      message: `myCheck "${checkName}" erstellen`,
      createdOn: new Date(),
      metadata: {
        mode: 'mycheck',
        isBootstrapPlaceholder: 'true',
      },
      sender: 'user',
      uiBlock: undefined,
      files: undefined,
      table: undefined,
    }, false);
  }

  private async ensureChatReadyForProject(projectId: number): Promise<boolean> {
    if (this.currentProject && this.currentProject !== projectId) {
      this.handleProjectChange(projectId);
    } else if (!this.isInitialized) {
      this.currentProject = projectId;
      this.initializeChat();
    }

    if (this.currentProject !== projectId) {
      this.currentProject = projectId;
    }

    if (this.isChatReady()) return true;
    return this.waitForChatClient();
  }

  private async waitForChatClient(retries = 40, delayMs = 100): Promise<boolean> {
    if (this.isChatReady()) return true;
    if (retries <= 0) return false;

    await new Promise(resolve => window.setTimeout(resolve, delayMs));
    return this.waitForChatClient(retries - 1, delayMs);
  }

  private extractUiBlockFromChannelData(rawChannelData: string | undefined): string | undefined {
    if (!rawChannelData) return undefined;
    try {
      const parsedChannelData = JSON.parse(rawChannelData);
      const uiBlock = parsedChannelData?.uiBlock;
      if (!uiBlock) return undefined;
      return typeof uiBlock === 'string' ? uiBlock : JSON.stringify(uiBlock);
    } catch {
      return undefined;
    }
  }

  private getMycheckBootstrapKey(projectId: number, rulesetId: string): string {
    return `${projectId}:${rulesetId}`;
  }

  private readMycheckBootstrapQueue(): Record<string, string> {
    try {
      const rawQueue = sessionStorage.getItem(this.mycheckBootstrapQueueStorageKey);
      if (!rawQueue) return {};
      const parsedQueue = JSON.parse(rawQueue);
      return parsedQueue && typeof parsedQueue === 'object' ? parsedQueue : {};
    } catch {
      return {};
    }
  }

  private writeMycheckBootstrapQueue(queue: Record<string, string>): void {
    try {
      if (Object.keys(queue).length === 0) {
        sessionStorage.removeItem(this.mycheckBootstrapQueueStorageKey);
        return;
      }
      sessionStorage.setItem(this.mycheckBootstrapQueueStorageKey, JSON.stringify(queue));
    } catch {
      // Ignore storage write failures and continue without persisted queue.
    }
  }

  /**
   * Die offene UI-Block-Auswahl als typisiertes Objekt.
   *
   * <p>Gegenstück zu {@link getUiBlockSelectionMetadata}, das dieselbe Angabe als flache Strings für
   * den alten ACS-Weg lieferte. Über den neuen Transport geht sie als Teil des Kontextobjekts.
   */
  private getUiBlockSelection(session: ChatSession) {
    const selection = session.pendingUiBlockSelection;
    if (!selection) return null;
    return {
      messageId: selection.messageId ?? null,
      blockIndex: selection.blockIndex,
      type: selection.uiBlockType,
      optionValues: selection.optionValues,
    };
  }

  /** Normalisiert den myCheck-Typ eines Stream-Signals; 'KI' ist die sichere Annahme. */
  private normalizeMycheckType(raw: unknown): 'KI' | 'IDS' | 'HYBRID' {
    const normalized = typeof raw === 'string' ? raw.trim().toUpperCase() : '';
    if (normalized === 'IDS' || normalized === 'KI' || normalized === 'HYBRID') return normalized;
    return this.activeMycheckType ?? 'KI';
  }

  private getUiBlockSelectionMetadata(session: ChatSession): Record<string, string> {
    if (!session.pendingUiBlockSelection) return {};
    const {messageId, blockIndex, uiBlockType, optionValues} = session.pendingUiBlockSelection;
    return {
      uiBlockSelectionMessageId: messageId ?? '',
      uiBlockSelectionBlockIndex: blockIndex.toString(),
      uiBlockSelectionType: uiBlockType,
      uiBlockSelectionOptionValues: JSON.stringify(optionValues),
    };
  }

  private parseUiBlock(rawUiBlock: string): UiBlock[] | undefined {
    try {
      const parsed = JSON.parse(rawUiBlock);
      const normalized = this.normalizeUiBlocks(parsed);
      if (!normalized || normalized.length === 0) {
        if (parsed == null || (Array.isArray(parsed) && parsed.length === 0)) {
          return undefined;
        }
        console.warn('Invalid uiBlock payload received from chat message metadata');
        return undefined;
      }
      return normalized;
    } catch {
      console.warn('Could not parse uiBlock payload from chat message metadata');
      return undefined;
    }
  }

  private normalizeUiBlocks(value: unknown): UiBlock[] | undefined {
    if (value == null) return undefined;

    const candidates = Array.isArray(value) ? value : [value];
    const validBlocks = candidates.filter(candidate => this.isUiBlock(candidate));

    if (validBlocks.length === 0) return undefined;
    if (validBlocks.length !== candidates.length) {
      console.warn('Received partially invalid uiBlock payload; keeping valid blocks only');
    }

    return validBlocks;
  }

  private isUiBlock(value: unknown): value is UiBlock {
    if (!value || typeof value !== 'object') return false;
    const candidate = value as Partial<UiBlock>;
    if (!candidate.type || !['question', 'options', 'summary', 'unavailable'].includes(candidate.type)) return false;

    if (candidate.options && !Array.isArray(candidate.options)) return false;
    if (candidate.options && candidate.options.some(option =>
      typeof option?.label !== 'string' || typeof option?.value !== 'string'
    )) return false;

    if (candidate.multiSelect != null && typeof candidate.multiSelect !== 'boolean') return false;
    if (candidate.question != null && typeof candidate.question !== 'string') return false;
    if (candidate.summary != null && typeof candidate.summary !== 'string') return false;
    if (candidate.summaryFields && !Array.isArray(candidate.summaryFields)) return false;
    if (candidate.summaryFields && candidate.summaryFields.some(field =>
      typeof field?.title !== 'string'
      || !Array.isArray(field?.bulletPoints)
      || field.bulletPoints.some(point => typeof point !== 'string')
    )) return false;
    if (candidate.unavailableText != null && typeof candidate.unavailableText !== 'string') return false;
    if (candidate.shouldImplement != null && typeof candidate.shouldImplement !== 'boolean') return false;

    return true;
  }

  /**
   * Das myCheck, für das in diesem Projekt gerade ein Quickstart läuft – falls es eines gibt.
   *
   * <p>Damit nimmt die Übersicht den Chat nach einem Reload wieder auf, statt den laufenden Vorgang
   * verschwinden zu lassen.
   */
  getActiveMycheckQuickstart(): { rulesetId: string; name: string } | undefined {
    const projectId = this.stateConfigService.getProjectId();
    if (!projectId) return undefined;
    const prefix = `${projectId}:`;
    const active = this.readMycheckQuickstartActive();
    const key = Object.keys(active).find(entry => entry.startsWith(prefix));
    if (!key) return undefined;
    return {rulesetId: key.slice(prefix.length), name: active[key] ?? ''};
  }

  /**
   * True, wenn sich ein gemerkter Quickstart zuverlässig wieder aufnehmen lässt.
   *
   * <p>Zweite Sicherung neben dem Schlüssel in der URL: der Aktiv-Merker kann auf eine Session
   * zeigen, deren Thread es nicht mehr gibt. `ensureMycheckThread` legte in dem Fall einen neuen,
   * leeren Thread an – genau der leere Chat, in dem Nutzer statt in der Übersicht landeten.
   * Wiederaufnehmbar ist eine Session nur, wenn entweder noch eine Startnachricht aussteht oder
   * der gemerkte Thread auflösbar ist und zu diesem myCheck gehört.
   *
   * <p>Fehler beim Auflösen (z.B. 429) reisen bewusst weiter: sie bedeuten "unbekannt", nicht
   * "nicht vorhanden", und dürfen keinen Abbruch der laufenden Session auslösen.
   */
  async canResumeMycheckQuickstart(rulesetId: string): Promise<boolean> {
    if (!rulesetId) return false;
    if (this.hasQueuedMycheckBootstrap(rulesetId)) return true;
    // Ein Aufruf statt "gemerkte Thread-ID lesen und ihre Metadata gegenpruefen": die Bindung
    // Verlauf-zu-myCheck traegt jetzt ein Index in der Datenbank, nicht mehr die Thread-Metadata.
    return (await this.chatApiService.threadByRuleset(rulesetId)) !== null;
  }

  /**
   * Erzeugt einen Platzhalter-Schlüssel für einen Quickstart, dessen myCheck es noch nicht gibt.
   *
   * <p>Nötig, weil der Typ (KI vs. IDS) erst im Interview fällt und der myCheck erst danach
   * angelegt wird – vorher gibt es keine ID, an der Thread und Aktiv-Merker hängen könnten.
   */
  startPendingMycheckQuickstart(): string {
    const key = `${ChatService.PENDING_MYCHECK_PREFIX}${crypto.randomUUID()}`;
    this.pendingMycheckKey = key;
    this.activeMycheckRulesetId = undefined;
    this.activeMycheckType = undefined;
    return key;
  }

  /**
   * Setzt den Quickstart-Zustand zurück (ID, Typ, Platzhalter).
   *
   * <p>An einer Stelle gebündelt, weil ein zurückgesetztes `activeMycheckRulesetId` ohne den
   * Platzhalter dazu führte, dass `activeMycheckKey` noch einen alten Chat adressierte.
   */
  resetMycheckQuickstartState(): void {
    this.activeMycheckRulesetId = undefined;
    this.activeMycheckType = undefined;
    this.pendingMycheckKey = undefined;
    window.clearTimeout(this.mycheckSeriesTimer);
    this.mycheckSeriesPaused = false;
    this.mycheckSeriesProgressed = false;
    this.applyMycheckSeries(undefined);
  }

  /** Gemerkter Typ eines myChecks, falls vorhanden. */
  getRememberedMycheckType(mycheckKey: string): 'KI' | 'IDS' | 'HYBRID' | undefined {
    const projectId = this.stateConfigService.getProjectId();
    if (!projectId || !mycheckKey) return undefined;
    const stored = this.readStorageMap(this.mycheckQuickstartTypeStorageKey)[
      this.getMycheckBootstrapKey(projectId, mycheckKey)
      ];
    return stored === 'IDS' || stored === 'KI' || stored === 'HYBRID' ? stored : undefined;
  }

  /**
   * Übernimmt den myCheck, den der Agent gerade angelegt hat, und bindet den laufenden Chat um.
   *
   * <p>Alles, was am Platzhalter hing, wandert auf die echte ID: die Thread-Zuordnung (lokal **und**
   * in der ACS-Thread-Metadata, sonst findet ein Reload den Thread nicht mehr), der Aktiv-Merker
   * und eine noch offene Bootstrap-Vormerkung.
   */
  async adoptCreatedMycheck(rulesetId: string, mycheckType: 'KI' | 'IDS' | 'HYBRID'): Promise<void> {
    if (!rulesetId || this.activeMycheckRulesetId === rulesetId) {
      this.activeMycheckType = mycheckType;
      return;
    }
    // Der bisherige Schlüssel: der Platzhalter vor dem ersten myCheck – oder in einer Serie der
    // zuvor angelegte myCheck, an dem Aktiv-Merker und URL bis jetzt hingen.
    const pendingKey = this.pendingMycheckKey ?? this.activeMycheckRulesetId;
    this.activeMycheckRulesetId = rulesetId;
    this.activeMycheckType = mycheckType;
    this.pendingMycheckKey = undefined;

    const threadId = this.sessions.mycheck.currentChat?.id;
    if (threadId) {
      this.rememberMycheckThreadId(rulesetId, threadId);
      await this.rebindThreadToMycheck(threadId, rulesetId);
    }

    const projectId = this.stateConfigService.getProjectId();
    if (pendingKey && projectId) {
      this.forgetMycheckThreadId(pendingKey);
      const active = this.readMycheckQuickstartActive();
      const pendingStorageKey = this.getMycheckBootstrapKey(projectId, pendingKey);
      const name = active[pendingStorageKey];
      if (name !== undefined) {
        delete active[pendingStorageKey];
        active[this.getMycheckBootstrapKey(projectId, rulesetId)] = name;
        this.writeStorageMap(this.mycheckQuickstartActiveStorageKey, active);
      }
      // Eine noch nicht verschickte Bootstrap-Nachricht mitnehmen, sonst bleibt sie als Leiche
      // unter dem Platzhalter liegen.
      const queue = this.readMycheckBootstrapQueue();
      const queuedName = queue[pendingStorageKey];
      if (queuedName !== undefined) {
        delete queue[pendingStorageKey];
        queue[this.getMycheckBootstrapKey(projectId, rulesetId)] = queuedName;
        this.writeMycheckBootstrapQueue(queue);
      }
    }

    this.rememberMycheckType(rulesetId, mycheckType);
    this.mycheckRulesetCreated.emit({rulesetId, type: mycheckType});
  }

  /**
   * Quickstart abgeschlossen: Aktiv-Merker zentral entfernen, dann `mycheckRulesetCompleted`
   * melden. Gemeinsamer Abschluss für Stream-Signal und Bot-Metadata.
   *
   * <p>Zentral, weil der Merker sonst liegen blieb, wenn beim Abschluss kein Editor gemountet war –
   * der Editor zeigte beim Öffnen weiter „Quickstart läuft". Aufrufer müssen eine laufende
   * {@link adoptCreatedMycheck} vorher abgewartet haben, und `pendingKey` ist der Platzhalter von
   * **vor** der Übernahme (die setzt ihn zurück).
   *
   * @param rulesetId die gemeldete ID – so geht sie an die Abonnenten
   * @param markerKey Schlüssel des Aktiv-Merkers; ohne gemeldete ID der des offenen Quickstarts
   */
  private finishMycheckQuickstart(rulesetId: string | undefined, markerKey: string | undefined,
                                  pendingKey: string | undefined): void {
    if (markerKey) {
      this.forgetMycheckQuickstart(markerKey);
    }
    // Der Platzhalter zusätzlich: wurde er nicht gegen die echte ID getauscht, bliebe sonst ein
    // Merker ohne myCheck zurück.
    if (pendingKey && pendingKey !== markerKey) {
      this.forgetMycheckQuickstart(pendingKey);
    }
    this.mycheckRulesetCompleted.emit(rulesetId);
  }

  /** Quickstart für dieses myCheck beendet – der Editor öffnet künftig direkt das Formular. */
  forgetMycheckQuickstart(rulesetId: string): void {
    const projectId = this.stateConfigService.getProjectId();
    if (!projectId || !rulesetId) return;
    const key = this.getMycheckBootstrapKey(projectId, rulesetId);
    const active = this.readMycheckQuickstartActive();
    delete active[key];
    this.writeStorageMap(this.mycheckQuickstartActiveStorageKey, active);
    const types = this.readStorageMap(this.mycheckQuickstartTypeStorageKey);
    if (key in types) {
      delete types[key];
      this.writeStorageMap(this.mycheckQuickstartTypeStorageKey, types);
    }
  }

  /**
   * Stößt die myCheck-Erstellung an.
   *
   * <p>Eine gewöhnliche Frage über den Stream. Der frühere Weg baute eine Bot-Framework-Aktivität
   * nach und schickte sie als verschachteltes JSON in der Nachrichten-Metadata – samt Token, weil
   * der Agent sonst keine Werkzeuge hatte. Beides entfällt: Modus und Regelwerk stehen am Thread,
   * der Nutzer am Request.
   */
  /**
   * Lässt den Agenten den besprochenen myCheck jetzt anlegen – der Weg hinter „Übernehmen".
   *
   * <p>Geht als gewöhnliche Nachricht samt Auswahl {@link MYCHECK_CONFIRM_CREATE}, also genau so,
   * als hätte der Nutzer im Chat „myCheck anlegen" gewählt: angelegt wird ausschließlich über die
   * Werkzeuge des Agenten, nicht an ihm vorbei.
   */
  async requestMycheckCreation(): Promise<void> {
    const threadId = this.sessions.mycheck.currentChat?.id;
    if (!threadId) {
      throw new Error('Kein Quickstart-Chat offen – es gibt nichts zu übernehmen.');
    }
    this.registerUiBlockSelection('mycheck', [ChatService.MYCHECK_CONFIRM_CREATE], 0, 'options');
    await this.sendMessage('mycheck', threadId, 'myCheck anlegen');
  }

  private async sendMycheckBootstrapActivity(chatThreadId: string, checkName: string): Promise<void> {
    const text = `${ChatService.MYCHECK_BOOTSTRAP_PREFIX} "${checkName}". `
      + 'Schlage mir als ersten Schritt passende Auswahloptionen vor, basierend auf dem Namen.';

    this.addMycheckBootstrapPlaceholderMessage(checkName);
    await this.sendMessage('mycheck', chatThreadId, text, {isBootstrap: 'true'});
  }

  /** Merkt den gewählten Typ, damit ein Reload ihn nicht verliert. */
  private rememberMycheckType(mycheckKey: string, type: 'KI' | 'IDS' | 'HYBRID'): void {
    const projectId = this.stateConfigService.getProjectId();
    if (!projectId || !mycheckKey) return;
    const types = this.readStorageMap(this.mycheckQuickstartTypeStorageKey);
    types[this.getMycheckBootstrapKey(projectId, mycheckKey)] = type;
    this.writeStorageMap(this.mycheckQuickstartTypeStorageKey, types);
  }

  /** Die Oberfläche, in der dieser Verlauf offen ist – er kann in beiden liegen. */
  private sessionForThread(threadId: string): ChatSession | undefined {
    if (!threadId) return undefined;
    const active = this.sessions[this.activeSurface];
    if (active.currentChat?.id === threadId) return active;
    const others = (Object.keys(this.sessions) as ChatSurface[]).filter(surface => surface !== this.activeSurface);
    for (const surface of others) {
      const session = this.sessions[surface];
      if (session.currentChat?.id === threadId) return session;
    }
    return undefined;
  }

  /**
   * Bindet den Verlauf an das tatsächlich angelegte Regelwerk.
   *
   * <p>Das erledigt jetzt der Server: er kennt den Ausgang des Werkzeugaufrufs und schreibt die
   * Bindung beim Abschluss des Turns fest. Die Oberfläche merkt sich nur noch den aktiven Stand –
   * die frühere Umbindung der Thread-Metadata samt Zwischenspeicher entfällt.
   */
  private async rebindThreadToMycheck(threadId: string, rulesetId: string): Promise<void> {
    this.rememberMycheckThreadId(rulesetId, threadId);
  }

  /**
   * Wertet das Abschluss-Signal des Quickstart aus: der myCheck ist vollständig und startbar.
   *
   * <p>Der Bot sendet das Flag in channelData; ACS liefert es je nach Pfad flach in der Metadata
   * oder nur im channelData-JSON. Beide Wege werden gelesen – vorher wurde nur die flache Variante
   * geprüft, wodurch das Signal verloren gehen konnte.
   */
  private handleRealtimeBotMetadata(metadata: Record<string, string> | undefined): void {
    const channelData = this.parseChannelData(metadata?.['channelData'])
      ?? this.parseChannelData(metadata?.['channel_data']);

    const reportedRulesetId = metadata?.['rulesetId']
      ?? (typeof channelData?.['rulesetId'] === 'string' ? channelData['rulesetId'] as string : undefined);

    // Anlage-Signal zuerst: der Agent hat den Typ gewählt und den myCheck geschrieben. Ab hier
    // kennt das Frontend ID und Editor-Route – vorher lief der Quickstart auf einem Platzhalter.
    const created = this.isTruthyMetadataFlag(metadata?.['ruleset_created'])
      || this.isTruthyFlag(channelData?.['ruleset_created']);
    // Vor der Übernahme festhalten: sie setzt den Platzhalter zurück.
    const pendingKey = this.pendingMycheckKey;
    let adopted: Promise<void> = Promise.resolve();
    if (created && reportedRulesetId && !this.createdSignalsHandled.has(reportedRulesetId)) {
      this.createdSignalsHandled.add(reportedRulesetId);
      adopted = this.adoptCreatedMycheck(reportedRulesetId, this.parseMycheckType(metadata, channelData));
    } else if (reportedRulesetId && reportedRulesetId === this.activeMycheckRulesetId) {
      // Kein Ereignis, nur Kontext: der Typ reist in jeder Bot-Antwort mit, damit ein Reload
      // mitten im Gespräch ihn nicht verliert.
      this.activeMycheckType = this.parseMycheckType(metadata, channelData);
    }

    // Vorschlags-Signal (Edit-Modus): nur beim Übergang feuern, sonst würde jede Folgeantwort, die
    // noch dieselbe PENDING proposalId nennt, das Event erneut auslösen.
    const proposalId = metadata?.['mycheck_proposal_id']
      ?? (typeof channelData?.['mycheck_proposal_id'] === 'string' ? channelData['mycheck_proposal_id'] as string : undefined);
    const proposalCreated = this.isTruthyMetadataFlag(metadata?.['mycheck_proposal_created'])
      || this.isTruthyFlag(channelData?.['mycheck_proposal_created']);
    if (proposalCreated && proposalId && reportedRulesetId && !this.proposalSignalsHandled.has(proposalId)) {
      this.proposalSignalsHandled.add(proposalId);
      this.mycheckProposalReceived.emit({rulesetId: reportedRulesetId, proposalId});
    }

    const completed = this.isTruthyMetadataFlag(metadata?.['ruleset_edited'])
      || this.isTruthyFlag(channelData?.['ruleset_edited']);
    if (!completed) return;

    const rulesetId = reportedRulesetId ?? this.activeMycheckRulesetId;

    // Nur einmal je myCheck melden: das Signal kann jetzt über mehrere Wege eintreffen
    // (Realtime-Benachrichtigung und Verlauf-Nachladen).
    const signalKey = rulesetId ?? 'unknown';
    if (this.completedSignalsHandled.has(signalKey)) return;
    this.completedSignalsHandled.add(signalKey);

    // Zentral aufräumen statt nur in der gerade sichtbaren Ansicht: kam das Signal an, während
    // weder Übersicht noch Editor gemountet waren, blieb der Aktiv-Merker im sessionStorage
    // liegen und der nächste Aufruf von `/my-checks` nahm eine längst fertige Session wieder auf.
    // Erst nach einer Übernahme aus derselben Nachricht – wie im Stream-Pfad.
    const finishedKey = rulesetId ?? this.activeMycheckKey;
    void adopted.finally(() => this.finishMycheckQuickstart(rulesetId, finishedKey, pendingKey));
  }

  /**
   * Liest den myCheck-Typ aus der Bot-Antwort; Fallback ist der bisher bekannte Typ, dann 'KI'.
   *
   * <p>'KI' als letzte Annahme ist bewusst gewählt: sie führt in den KI-Editor, der jeden myCheck
   * lesbar anzeigt, während ein falsch geratenes 'IDS' den Nutzer in einen Editor schickt, der
   * nichts findet.
   */
  private parseMycheckType(
    metadata: Record<string, string> | undefined,
    channelData: Record<string, unknown> | undefined,
  ): 'KI' | 'IDS' | 'HYBRID' {
    const raw = metadata?.['mycheckType']
      ?? (typeof channelData?.['mycheckType'] === 'string' ? channelData['mycheckType'] as string : undefined);
    const normalized = raw?.trim().toUpperCase();
    if (normalized === 'IDS' || normalized === 'KI' || normalized === 'HYBRID') return normalized;
    return this.activeMycheckType ?? 'KI';
  }

  /**
   * Findet den Chat-Thread eines myChecks – mit so wenigen ACS-Aufrufen wie möglich.
   *
   * <p>Reihenfolge: gemerkte Thread-ID (ein Aufruf) → begrenzte Suche über die zuletzt genutzten
   * Threads. Parallele Aufrufe für dasselbe myCheck teilen sich eine laufende Suche.
   */
  private async findMycheckThread(rulesetId: string): Promise<ChatThreadEntry | undefined> {
    const running = this.mycheckThreadLookups.get(rulesetId);
    if (running) return running;

    const lookup = this.lookupMycheckThread(rulesetId)
      .finally(() => this.mycheckThreadLookups.delete(rulesetId));
    this.mycheckThreadLookups.set(rulesetId, lookup);
    return lookup;
  }

  /**
   * Der Verlauf eines myChecks.
   *
   * <p>Ein Aufruf statt eines Scans. Bisher lief die Suche über bis zu 40 ACS-Threads, fragte je
   * Thread die Eigenschaften ab und musste sich das Ergebnis im `sessionStorage` merken, um das
   * nicht bei jedem Aufruf zu wiederholen. Die Eindeutigkeit trägt jetzt ein Index in der Datenbank.
   */
  private async lookupMycheckThread(rulesetId: string): Promise<ChatThreadEntry | undefined> {
    const thread = await this.chatApiService.threadByRuleset(rulesetId);
    if (!thread) return undefined;
    // Nach einem Neuladen: der Stand einer Serie steht am Verlauf, nicht im Speicher des Browsers.
    if (thread.mode === 'MYCHECK' && thread.mycheckSeries && rulesetId === this.activeMycheckKey) {
      this.applyMycheckSeries(thread.mycheckSeries);
    }
    return {
      id: thread.id,
      topic: thread.topic,
      createdOn: new Date(thread.createdAt),
    };
  }

  private readMycheckQuickstartActive(): Record<string, string> {
    return this.readStorageMap(this.mycheckQuickstartActiveStorageKey);
  }

  private readMycheckThreadId(rulesetId: string): string | undefined {
    const projectId = this.stateConfigService.getProjectId();
    if (!projectId || !rulesetId) return undefined;
    return this.readMycheckThreadMap()[this.getMycheckBootstrapKey(projectId, rulesetId)];
  }

  private rememberMycheckThreadId(rulesetId: string, threadId: string): void {
    const projectId = this.stateConfigService.getProjectId();
    if (!projectId || !rulesetId || !threadId) return;
    const map = this.readMycheckThreadMap();
    map[this.getMycheckBootstrapKey(projectId, rulesetId)] = threadId;
    this.writeMycheckThreadMap(map);
  }

  private forgetMycheckThreadId(rulesetId: string): void {
    const projectId = this.stateConfigService.getProjectId();
    if (!projectId || !rulesetId) return;
    const map = this.readMycheckThreadMap();
    delete map[this.getMycheckBootstrapKey(projectId, rulesetId)];
    this.writeMycheckThreadMap(map);
  }

  private readMycheckThreadMap(): Record<string, string> {
    return this.readStorageMap(this.mycheckThreadMapStorageKey);
  }

  private writeMycheckThreadMap(map: Record<string, string>): void {
    this.writeStorageMap(this.mycheckThreadMapStorageKey, map);
  }

  private readStorageMap(key: string): Record<string, string> {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private writeStorageMap(key: string, map: Record<string, string>): void {
    try {
      if (Object.keys(map).length === 0) {
        sessionStorage.removeItem(key);
        return;
      }
      sessionStorage.setItem(key, JSON.stringify(map));
    } catch {
      // Ohne persistierte Zuordnung funktioniert alles weiter, nur mit Thread-Suche.
    }
  }

  private isClassicThreadForCurrentProject(metadata: Record<string, string> | undefined): boolean {
    return metadata?.['projectId'] == this.currentProject.toString() && this.getThreadMode(metadata) === 'classic';
  }

  /**
   * Ein Thread gehört zu diesem myCheck unabhängig davon, ob er aus dem Quickstart ('mycheck') oder
   * aus dem Edit-Modus ('edit') stammt – beides ist derselbe "ein Thread pro myCheck", den
   * {@link ensureMycheckThread} wiederfinden bzw. anlegen soll. Ohne diese Erweiterung würde ein im
   * Edit-Modus neu angelegter Thread beim nächsten Aufruf nicht wiedererkannt und dupliziert.
   */
  private isMycheckThreadForRuleset(metadata: Record<string, string> | undefined, rulesetId: string): boolean {
    const mode = this.getThreadMode(metadata);
    return metadata?.['projectId'] == this.currentProject.toString()
      && (mode === 'mycheck' || mode === 'edit')
      && metadata?.['rulesetId'] === rulesetId;
  }

  private getThreadMode(metadata: Record<string, string> | undefined): ChatThreadMode {
    const mode = metadata?.['mode'];
    if (mode === 'mycheck') return 'mycheck';
    if (mode === 'edit') return 'edit';
    return 'classic';
  }

}
